// Import debounce
import debounce from 'debounce';

// Import global utils
import { getUniqueID } from '@automator/shared/utils/get-unique-id';
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x, sprintf } from '@wordpress/i18n';

// eslint-disable-next-line import/no-unresolved
import $ from 'jquery';

// Import select2 (Dropdown)
// select2.org
import 'select2';

// Import flatpickr (DatePicker)
// flatpickr.js.org
import flatpickr from 'flatpickr';

import {
	AJAXRequest,
	getUnsavedFieldsValues,
	getField,
	inArray,
	isCustomValue
} from '../common/utilities.js';

import {
	fieldTypes as validFieldTypes
} from '@automator/builder/_legacy/js/config/valid-data';

import { validateField } from '../validation/validation.js';

import TokenSelector from '../tokens/token-selector.js';

// Import Field components.
import './components/index.js';

// Import Field utilities
import { generateTinyMCEConfig } from './utilities/index.js';

/**
 * Create a field
 *
 * @since 0.4
 * @param {String}              type                     - Field type
 * @param {object}              attributes               - Object with information about the field
 * @param {String}              attributes.itemType      Item type. Valid values are trigger, action and closure.
 * @param {int}                 attributes.itemId        ID of the recipe item
 * @param {(null|string|array)} attributes.defaultValue  Default value of the field
 * @param {String}              attributes.currentValue  Current value of the field
 * @param {String}              attributes.placeholder   Placeholder of the field
 * @param {String}              attributes.isHidden      Defines if the field should be hidden
 * @param {String}              attributes.label         Label of the field
 * @param {String}              attributes.description   Description of the field
 * @param {object}              attributes.options       Only used if the field is a select. All the options of the select.
 * @param {null|string}         [attributes.color]       Only used for checkboxes. Valid values are null, 'gutenberg' or 'green'
 * @param {null|string}         [attributes.align]       Only used for checkboxes. Valid values are null, 'left' or 'right'
 * @param {object}              [attributes.toggle]      Only used for checkboxes. Has On and Off strings
 * @param {String}              [attributes.toggle.on]   String shown when the toggle is selected
 * @param {String}              [attributes.toggle.off]  String shown when the toggle is unselected
 * @param {Boolean}             attributes.isAjax        Only used if the field is a select. Add listner for the AJAX request.
 * @param {(null|string|array)} attributes.targetField   Only used if the field is a select. AJAX return values and populate this field.
 * @param {String}              attributes.integrationID Integration of plugin
 * @param {String}              attributes.endPoint      endPoint for Ajax Call
 */

class Field {
	constructor( type, attributes, createDOMelements = true, customValidationFunction = null ) {
		this.type = type;

		this.attributes = this.setAttributes( attributes );
		this.createDOMelements = createDOMelements;
		this.customValidationFunction = customValidationFunction;

		if ( createDOMelements ) {
			this.createFormElement();
		}
	}

	/**
	 * Create a jQuery DOM element with the field
	 *
	 * @since 0.3
	 */

	createFormElement( initLibraries = true ) {
		// Create response object
		let response = null;

		// Local variables
		const type = this.type;
		const attributes = this.attributes;

		// Check if it's a valid field type
		if ( inArray( type, validFieldTypes ) ) {
			// Create form element container, this is global
			const formElement = $( '<div/>', { class: 'form-element' } );

			// Add identifiers
			formElement.attr( 'data-integration-id', attributes.integrationID );
			formElement.attr( 'data-field-code', attributes.optionCode );

			// Check the user is trying to add a custom class to the formElement element
			if ( isDefined( attributes.formElementClass ) ) {
				formElement.addClass( attributes.formElementClass );
			}

			// Fields attributes, those will be added to the field jQuery object
			const fieldAttributes = {
				name: attributes.optionCode,
				id: attributes.uniqueFieldID,

				'data-isajax': attributes.isAjax,
				'data-targetfield': attributes.targetField,
				'data-integrationid': attributes.integrationID,
				'data-endPoint': attributes.endPoint,

				'data-supportsTokens': attributes.supportsTokens,
				'data-excludeDefaultToken' : attributes.excludeDefaultToken,
				'data-supportsTinymce': attributes.supportsTinyMCE,
				'data-required': attributes.isRequired,
				'data-item_type': attributes.itemType,
				'data-item-id': attributes.itemId,

				'data-option-group': attributes.groupId,

				'data-is-hidden': attributes.isHidden,
				'data-is-child-field': attributes.isChildField,
			};

			// Check if it's a textarea
			if ( this.type === 'textarea' ) {
				fieldAttributes[ 'data-supportsfullpageediting' ] = attributes.supportsFullPageEditing;
				fieldAttributes[ 'data-allowed-html-tags' ] = attributes.allowedHtmlTags ? JSON.stringify( attributes.allowedHtmlTags ) : null;
				fieldAttributes[ 'data-supports-media' ] = attributes.supportsMedia;
			}

			// Define field container variable once. An different jQuery object will be created based on the field type
			let fieldContainer, field;

			// Create field based in field type
			switch ( type ) {
				case 'select':
					// Field container
					fieldContainer = $( '<div/>', {
						class: 'form-select',
					} );

					this.fieldContainer = fieldContainer;

					// Create select element and add fieldAttributes to jQuery object. Merge second object into fieldAttributes, recursively
					field = $(
						'<select/>',
						$.extend( {}, fieldAttributes, {
							'data-type': 'select',
							class: 'form-element__select',
							'data-supports-custom-value': attributes.supportsCustomValue,
						} )
					);

					// Check if it supports multiple values
					if ( attributes.supportsMultipleValues ) {
						// Make it a multiple select
						field.prop( 'multiple', 'multiple' );

						// Check if it supports custom value and if the value is an empty array
						if ( attributes.supportsCustomValue && isEmpty( attributes.currentValue ) ) {
							// Add empty value
							attributes.currentValue = [];
							// Set has value to false.
							attributes.hasValue = false;
						}
					}

					this.field = field;

					let hasSelectedOption = false,
						numberOfOptions = 0,
						defaultValue,
						defaultOptionExists;

					// Check if it has default option and get the default option's value
					if ( attributes.hasDefaultValue ) {
						if (
							! isEmpty( attributes.defaultValue.option_value ) &&
							! isEmpty( attributes.defaultValue.option_name )
						) {
							// The default option has value and name, so we have to add a new option instead of searching for one
							defaultValue = attributes.defaultValue.option_value;
						} else {
							defaultValue = attributes.defaultValue;
						}
					}

					// Create select options
					const $normalOptions = $(
						`<optgroup class="form-element__select-normal-options"></optgroup>`
					);

					// console.log( attributes.label );
					// console.log( '👉 Original value: ' + attributes.currentValue );

					// Remove the "automator_custom_value" option
					attributes.options = attributes.options.filter( ( option ) => {
						return ! isCustomValue( option.value );
					} );

					$.each( attributes.options, function( value, option_data ) {
						const selectOption = $( '<option/>', {
							value: option_data.value,
							text: option_data.text,
						} );

						// Check if one of the options value match with the default value. In that case, just select it instead of creating another one with the same value
						if ( attributes.hasDefaultValue && ( defaultValue == value ) ) {
							selectOption.prop( 'selected', true );
							defaultOptionExists = true;
							hasSelectedOption = true;
						}

						// But also check if the select has a value (and it's not empty), in that case we have to select that one
						if ( attributes.hasValue && ( attributes.currentValue == option_data.value ) ) {
							selectOption.prop( 'selected', true );
							hasSelectedOption = true;
						}

						selectOption.appendTo( $normalOptions );
						numberOfOptions++;
					} );
					// Append options to the field
					$normalOptions.appendTo( field );

					// ⚠️ Check if it's a multiselect
					if ( attributes.supportsMultipleValues ) {
						// If so, check if it's has a current value
						if ( ! isEmpty( attributes.currentValue ) ) {
							try {
								field.val( attributes.currentValue );
							} catch ( e ) {
								// Continue regardless of error

								// Output error if the developer mode is enabled
								if ( UncannyAutomator._site.has_debug_enabled ) {
									// eslint-disable-next-line no-console
									console.warn( e );
								}
							}
						}

						// Check if it has a selected value
						if ( ! isEmpty( field.val() ) ) {
							hasSelectedOption = true;
						}
					}

					// Check if an option was selected, if not then check if we have a default option that doesn't exist in the select, in that case, create one
					if ( attributes.hasDefaultValue && ! defaultOptionExists ) {
						// Create option
						if (
							! isEmpty( attributes.defaultValue.option_value ) &&
							! isEmpty( attributes.defaultValue.option_name )
						) {
							const defaultOption = $( '<option/>', {
								value: attributes.defaultValue.option_value,
								text: attributes.defaultValue.option_name,
							} );

							if (
								! attributes.hasValue &&
								attributes.defaultValue.option_value != attributes.currentValue
							) {
								defaultOption.prop( 'selected', true );
								hasSelectedOption = true;
							}

							// Add first (or second, if the select has a placeholder)
							defaultOption.prependTo( field );
							numberOfOptions++;
						}
					}

					// Check if we have to add a placeholder
					if ( attributes.hasPlaceholder ) {
						// Add empty option at the top
						field.prepend( '<option></option>' );

						// If nothing else is selected, then remove the selected
						// value of the select
						if ( ! attributes.hasDefaultValue && ! attributes.hasValue ) {
							field.find( 'option:selected' ).prop( 'selected', false );
						}
					}

					// If the field has AJAX that's triggered by searching options, select2 won't create the option, so
					// we have to create it even if it's not a valid option anymore
					if (
						// Has value and it's not custom
						attributes.hasValue &&
						! isCustomValue( attributes.currentValue ) &&
						// Has AJAX that's triggered by searching otpions
						isDefined( attributes.ajax ) &&
						isDefined( attributes.ajax.event ) &&
						attributes.ajax.event === 'search_options'
					) {
						const currentValues = Array.isArray( attributes.currentValue ) ? attributes.currentValue : [ attributes.currentValue ];

						currentValues.forEach( ( currentValue ) => {
							let label = UncannyAutomator.recipe.items[ attributes.itemId ].meta[ `${ attributes.optionCode }_readable` ];
							label = isDefined( label ) ? label : currentValue;

							try {
								label = this._helperFindMultiselectLabel( label, currentValue );
							} catch (e) {
								console.warn(e);
							}

							// Check if it's inside a repeater and we can get the label
							if ( isDefined( attributes.repeaterOptionCode ) && isDefined( attributes.repeaterRowIndex ) ) {
								try {
									label = UncannyAutomator.recipe.items[ attributes.itemId ].meta[ attributes.repeaterOptionCode ][ attributes.repeaterRowIndex ][ `${ attributes.optionCode }_readable` ];

									try {
										label = this._helperFindMultiselectLabel( label, currentValue );
									} catch ( e ) {
										console.warn( e );
									}
								} catch ( e ) {
									console.warn( e );
								}
							}

							$normalOptions.get( 0 ).insertAdjacentHTML( 'afterBegin', `
								<option value="${ currentValue }" selected="selected">
									${ label }
								</option>
							` );

							numberOfOptions++;
						} );

						hasSelectedOption = true;
					}

					// Check if this select supports the token selector
					// If it does, create the "Custom value" field
					if ( attributes.supportsCustomValue ) {
						let customFieldValue = attributes.customValue;

						// Create "Other options" section
						const $otherOptions = $( `
							<optgroup label="${ escapeHTML( __( 'Other options', 'uncanny-automator' ) ) }">
								<option value="automator_custom_value">${ escapeHTML( __( 'Use a token/custom value', 'uncanny-automator' ) ) }</option>
							</optgroup>
						` );

						// Append "Other options" to the field
						field.append( $otherOptions );

						// Check if we have to select it
						if (
							attributes.hasValue && 
							isCustomValue( attributes.currentValue )
						) {
							// Select that option
							$otherOptions.find( 'option' ).attr( 'selected', true );
							hasSelectedOption = true;
						}

						// Check if it doesn't have a selected value
						if ( attributes.hasValue && ! hasSelectedOption ) {
							// Select that option
							$otherOptions.find( 'option' ).attr( 'selected', true );
							hasSelectedOption = true;

							// Set the current value as the custom value
							customFieldValue = attributes.currentValue;
						}

						// Create the custom field
						this.customValueField = new Field( 'text', {
							parentID: attributes.parentID,
							itemId: attributes.itemId,
							itemType: attributes.itemType,

							isRequired: attributes.isRequired,
							isReadOnly: false,
							supportsTinyMCE: false,
							supportsCopyToClipboard: false,
							supportsTokens: attributes.supportsTokens,

							isAjax: false, //

							fieldType: 'text',
							optionCode: `${ attributes.optionCode }_custom`,

							label: '',
							description: attributes.hasCustomValueDescription
								? attributes.customValueDescription
								: undefined,
							placeholder: '',
							isHidden: false,
							tokenName: sprintf(
								/* translators: 1. Token name */
								escapeHTML( __( '%s (custom value)', 'uncanny-automator' ) ),
								attributes.label
							),

							defaultValue: null,
							currentValue: customFieldValue,

							isChildField: true,
							parentOptionCode: attributes.optionCode,
						} );
					}

					// Has a selected option
					this.selectHasSelectedOption = hasSelectedOption;

					// If it doesn't have any options, add a "No results" option
					if ( attributes.ajax?.event !== 'search_options' && numberOfOptions == 0 ) {
						const $noResults = $( new Option( escapeHTML( __( 'No results found', 'uncanny-automator' ) ), '', true, true ) );

						$noResults.prop( 'disabled', true );

						this.field.find( '.form-element__select-normal-options' ).append( $noResults );

						if ( ! this.attributes.supportsCustomValue ) {
							// Disable the select
							this.field.prop( 'disabled', true );
						}
					}

					// Bind changes
					field.on( 'change', function( event, customData ) {
						formElement.trigger( 'automator:field:change', customData );
					} );

					break;

				case 'text':
				case 'url':
				case 'email':
				case 'int':
				case 'float':
				case 'date':
				case 'time':
					// FALLTHROUGH: These fields share the same structure as all of them are input text. The validation is done by another function. For now, these fields types only have two differences, the CSS class added in fieldContainer and the type property of the attributes object added to the field variable.

					// Create CSS classes that will be used by the field container element. This will create classes like "form-input--url". They are not used for validation but are useful to add custom CSS to each field
					const fieldContainerClass = `form-input form-input--${ type }`;

					// Field container
					fieldContainer = $( '<div/>', {
						class: fieldContainerClass,
					} );

					// Add the CodeMirror field
					this.$fakeCodemirrorField = $( '<div/>', {
						class: 'form-element__input-cm',
					} );

					fieldContainer.append( this.$fakeCodemirrorField );

					this.fieldContainer = fieldContainer;

					// Create input element and add fieldAttributes to jQuery object. Merge second object into fieldAttributes, recursively
					field = $(
						'<input/>',
						$.extend( {}, fieldAttributes, {
							class: 'form-element__input',
							type: 'text',
							'data-type': type,
						} )
					);

					// Check if maxlength is defined
					if ( isDefined( attributes.maxLength ) ) {
						field.prop( 'maxlength', attributes.maxLength );
					}

					this.field = field;

					// Add tokens CSS class to field. This will be used to render the Token Selector once the element is added to the DOM
					if ( attributes.supportsTokens ) {
						fieldContainer.addClass( 'form-input--token' );
					}

					// Check if the field has a placeholder and add it
					if ( attributes.hasPlaceholder ) {
						field.prop( 'placeholder', attributes.placeholder );
					}

					// Check if the field has a value and add it
					if ( attributes.hasValue ) {
						field.prop( 'value', attributes.currentValue );
					} else {
						// Now that we now that the field doesn't have a value we can check if the developer defined a default value
						if ( attributes.hasDefaultValue ) {
							field.prop( 'value', attributes.defaultValue );
						}
					}

					// Add "clear" button to date and time fields
					if ( [ 'date', 'time' ].includes( attributes.fieldType ) ) {
						// Add button
						const $clearButton = $( `
								<div class="form-element__input-clear" uap-tooltip="${ escapeHTML( _x( 'Clear', 'Verb', 'uncanny-automator' ) ) }">
									<uo-icon id="xmark"></uo-icon>
								</div>
						` );

						// Add class to the container
						fieldContainer.addClass( 'form-input--has-clear' );

						// Append the clear button
						this.$fakeCodemirrorField.append( $clearButton );

						// Listen clicks to the button
						$clearButton.on( 'click', () => {
							this.clearValue();
						} );
					}

					// Check if it has min/max numbers
					if ( ( [ 'int', 'float' ] ).includes( attributes.fieldType ) ) {
						if ( ! isEmpty( attributes.minNumber ) ) {
							field.get( 0 ).setAttribute( 'data-min', attributes.minNumber );
						}

						if ( ! isEmpty( attributes.maxNumber ) ) {
							field.get( 0 ).setAttribute( 'data-max', attributes.maxNumber );
						}
					}

					// Check if the field supports 'copy to clipboard'
					if ( 
						isDefined( attributes.supportsCopyToClipboard ) 
						&& attributes.supportsCopyToClipboard
					) {
						
						// Add class to the container
						fieldContainer.addClass( 'form-input--has-copy' );
						
						// Create the copy button element
						const $copyButton = document.createElement('uo-click-to-copy');
						
						// Set getContent function to return the field value
						$copyButton.getContent = () => {
							return this.getValue();
						};

						// Set content-name for the success message
						if ( isDefined( attributes.label ) && ! isEmpty( attributes.label ) ) {
							$copyButton.setAttribute( 'content-name', attributes.label );
						}

						// Append the copy button
						this.$fakeCodemirrorField.append( $copyButton );
					}

					// Bind changes
					field.on( 'input', function() {
						formElement.trigger( 'automator:field:change' );
					} );

					break;

				case 'textarea':
					// Field container
					fieldContainer = $( '<div/>', { class: 'form-textarea' } );

					this.fieldContainer = fieldContainer;

					// Create textarea
					field = $(
						'<textarea/>',
						$.extend( true, fieldAttributes, {
							'data-type': 'textarea',
							class: 'form-element__textarea',
							'data-item_type': attributes.itemType,
						} )
					);

					// Check if maxlength is defined
					if ( isDefined( attributes.maxLength ) ) {
						field.prop( 'maxlength', attributes.maxLength );
					}

					this.field = field;

					// Add tokens CSS class to field. This will be used to render the Token Selector once the element is added to the DOM
					if ( attributes.supportsTokens ) {
						fieldContainer.addClass( 'form-textarea--token' );
					}

					// Check if the field has a placeholder and add it
					if ( attributes.hasPlaceholder ) {
						field.prop( 'placeholder', attributes.placeholder );
					}

					// Add the CodeMirror field
					// All textareas have a CodeMirror editor, independently on
					// whether they also have TinyMCE.
					this.$fakeCodemirrorField = $( '<div/>', {
						class: 'form-element__input-cm',
					} );

					fieldContainer.append( this.$fakeCodemirrorField );

					// Check if the field has a value and add it
					if ( attributes.hasValue ) {
						field.text( attributes.currentValue );
					} else {
						// Now that we now that the field doesn't have a value we can check if the developer defined a default value
						if ( attributes.hasDefaultValue ) {
							field.text( attributes.defaultValue );
						}
					}

					// Bind changes
					field.on( 'input', function() {
						formElement.trigger( 'automator:field:change' );
					} );

					break;

				case 'checkbox':
					// Field container
					fieldContainer = $( '<div/>', { class: 'form-checkbox' } );

					this.fieldContainer = fieldContainer;

					const checkboxLabel = $( '<label/>', { class: 'form-checkbox__container' } );
					const checkboxPseudo = $( '<div/>', { class: 'checkbox--show' } );

					// Create checkbox
					field = $(
						'<input/>',
						$.extend( {}, fieldAttributes, {
							type: 'checkbox',
							'data-type': 'checkbox',
							class: 'checkbox--hidden checkbox--primary',
						} )
					);

					// Check if checked by default
					if ( attributes.hasValue && attributes.currentValue ) {
						field.prop( 'checked', true );
					}

					this.field = field;

					// Add label
					if ( attributes.hasLabel ) {
						if ( isDefined( attributes.isToggle ) && attributes.isToggle ) {
							// Add label outside the switch
							fieldContainer[0].insertAdjacentHTML(
								'afterBegin',
								`
									<label
										for="${ fieldAttributes.id }"
										class="form-element__label"
									>
										${ attributes.label }
									</label>
								`
							);
						} else {
							// Add label inside the checkbox wrapper
							checkboxLabel[0].innerHTML = attributes.label;
						}
					}

					// Append elements
					fieldContainer.append( checkboxLabel );
					checkboxLabel.append( field );
					checkboxLabel.append( checkboxPseudo );

					// Check if we have to render a Toggle
					if ( isDefined( attributes.isToggle ) && attributes.isToggle ) {
						fieldContainer.addClass( 'form-checkbox--toggle' );

						// Check if we have to add on and off strings divs
						if ( ! isDefined( attributes.toggle ) ) {
							attributes.toggle = {
								on: escapeHTML( __( 'Yes', 'uncanny-automator' ) ),
								off: escapeHTML( __( 'No', 'uncanny-automator' ) )
							}
						}

						const checkboxToggleOn = $( '<span/>', {
							class: 'checkbox-label checkbox-label--on',
						} ).text( attributes.toggle.on );
						checkboxLabel.append( checkboxToggleOn );

						const checkboxToggleOff = $( '<span/>', {
							class: 'checkbox-label checkbox-label--off',
						} ).text( attributes.toggle.off );
						checkboxLabel.append( checkboxToggleOff );
					}

					// Define color
					if ( isDefined( attributes.color ) ) {
						// Check if the color is valid
						if ( inArray( attributes.color, [ 'gutenberg', 'green' ] ) ) {
							// Add class
							switch ( attributes.color ) {
								case 'gutenberg':
									fieldContainer.addClass( 'form-checkbox--toggle-gutenberg' );
									break;

								case 'green':
									fieldContainer.addClass( 'form-checkbox--toggle-green' );
									break;
							}
						} else {
							console.error( `The color ${ attributes.color } does not exists. Default color will be used instead.` );
						}
					}

					// Define align
					if ( isDefined( attributes.align ) ) {
						// Check if it's a valid value
						if ( inArray( attributes.align, [ 'left', 'right' ] ) ) {
							// Check if it's 'right', otherwise ignore, since 'left' doesn't needs a class
							if ( attributes.align == 'right' ) {
								// Add class
								fieldContainer.addClass( 'form-checkbox--toggle-right' );
							}
						} else {
							console.error( `The alignment ${ attributes.align } does not exists. "left" will be used instead.` );
						}
					}

					// Bind changes
					field.on( 'change', function( event, customData ) {
						formElement.trigger( 'automator:field:change', customData );
					} );

					// Change field variable with all the container
					field = checkboxLabel;

					break;

				case 'radio':
					// Field container
					fieldContainer = $( '<div/>', { class: 'form-radio' } );
					this.fieldContainer = fieldContainer;

					// Create array to save all the jQuery DOM elements
					let $fields = $(),
						$fieldContainers = $();

					// Iterate options
					$.each( attributes.options, function( index, option ) {
						// Create container
						const $formRadioContainer = $( '<label/>', { class: 'form-radio__container' } );

						// Add label
						$formRadioContainer.html( option.text );

						// Create radio
						const $field = $(
							'<input/>',
							$.extend( {}, fieldAttributes, {
								type: 'radio',
								'data-type': 'radio',
								class: 'radio--hidden radio--primary',

								value: option.value,
								name: fieldAttributes.id,
							} )
						);

						// Save radio
						$fields = $fields.add( $field );

						// Create fake radio
						const $fakeRadio = $( '<div/>', { class: 'radio--show' } );

						// Append elements
						$formRadioContainer.append( $field );
						$formRadioContainer.append( $fakeRadio );

						// Check if this field has a value, if it does, and the value
						// matches the input radio name, then check it
						if ( attributes.hasValue && attributes.currentValue == option.value ) {
							$field.prop( 'checked', true );
						}
						// Otherwise, check if it has a default value and use that
						else if (
							! attributes.hasValue &&
							attributes.hasDefaultValue &&
							attributes.defaultValue == option.value
						) {
							$field.prop( 'checked', true );
						}

						// Save container
						$fieldContainers = $fieldContainers.add( $formRadioContainer );
					} );

					// Save fields
					this.field = $fieldContainers;
					field = $fieldContainers;

					// Bind changes
					$fields.on( 'change', ( event, customData ) => {
						// Check if the value is different
						formElement.trigger( 'automator:field:change', customData );
					} );

					break;

				case 'file':
					// Field container
					fieldContainer = $( '<div/>', { class: 'form-file' } );
					this.fieldContainer = fieldContainer;

					// Create the file input
					field = $( '<uo-field-input-file/>', {
						multiple: attributes.supportsMultipleValues,
						required: attributes.isRequired,
						value: attributes.currentValue,
					} );

					if ( attributes.fileTypes ) {
						field.prop( 'fileTypes', attributes.fileTypes );
					}

					this.field = field;

					// Bind changes
					this.field.on( 'change', ( event, customData ) => {
						// Check if the value is different
						formElement.trigger( 'automator:field:change', customData );
					} );

					break;
			}

			// Append field jQuery object to field container
			fieldContainer.append( field );

			// Create label

			// Create Label, even if it's empty but the field is required, so we can add the asterisk
			let fieldLabel;

			if ( type != 'checkbox' && ( attributes.hasLabel || attributes.isRequired ) ) {
				fieldLabel = $( '<label/>', {
					class: 'form-element__label',
					for: attributes.uniqueFieldID,
				} );

				// Add label text
				if ( attributes.hasLabel ) {
					fieldLabel.html( attributes.label );
				}
			}

			// Check if it's a required field
			if ( attributes.isRequired ) {
				// Add required class to the field container
				fieldContainer.addClass( 'form-element--required' );

				// Check if it's a checkbox
				if ( attributes.fieldType == 'checkbox' ) {
					// Add required attribute to the field
					field.find( 'input' ).prop( 'required', true );

					// Add asterisk to the label
					if ( attributes.hasLabel ) {
						// Add required icon next to label
						const fieldLabelAsterisk = $( '<span/>', { class: 'form-element-label__required' } );
						field.append( fieldLabelAsterisk );
					}
				} else {
					// Add required attribute to the field
					field.prop( 'required', true );

					// Add asterisk to the label
					if ( attributes.hasLabel ) {
						// Add required icon next to label
						const fieldLabelAsterisk = $( '<span/>', { class: 'form-element-label__required' } );
						fieldLabel.append( fieldLabelAsterisk );
					}
				}
			}

			// Check if it's read only
			if ( isDefined( attributes.isReadOnly ) && attributes.isReadOnly ) {
				// Check if it's NOT a date or time field
				if ( ! [ 'date', 'time' ].includes( this.attributes.fieldType ) ) {
					// Add required class to the field container
					fieldContainer.addClass( 'form-element--readonly' );
				}

				// Check if it's a select
				if ( attributes.fieldType == 'select' ) {
					// Disable the select
					field.prop( 'disabled', true );
				} else if ( [ 'checkbox', 'radio' ].includes( attributes.fieldType ) ) {
					// Disable the radio and checkbox
					field.find( 'input' ).prop( 'disabled', true );
				} else {
					// Add read only attribute to the field
					field.prop( 'readonly', true );
				}
			}

			// Append label element if is defined
			if ( attributes.hasLabel || attributes.isRequired ) {
				fieldContainer.prepend( fieldLabel );
			}

			// Error container
			const fieldError = $( '<div/>', { class: 'form-element__error' } );
			fieldContainer.append( fieldError );

			this.fieldContainer = fieldContainer;

			// Field description
			if ( attributes.hasDescription ) {
				const fieldDescription = $( '<div/>', { class: 'description' } );
				fieldDescription.html( attributes.description );
				fieldContainer.append( fieldDescription );
			}

			// Add button to manually trigger AJAX call again
			if (
				isDefined( attributes.ajax ) &&
				isDefined( attributes.ajax.event ) &&
				[ 'parent_fields_change', 'on_load' ].includes( attributes.ajax.event )
			) {
				fieldLabel.get( 0 ).insertAdjacentHTML( 'beforeEnd', `
					<uo-button
						color="secondary"
						size="extra-small"
						class="form-element-ajax-refresh-btn"
					>
						<uo-icon id="rotate"></uo-icon>

						${ this.type === 'select' ? escapeHTML( __( 'Refresh options', 'uncanny-automator' ) ) : escapeHTML( __( 'Refresh', 'uncanny-automator' ) ) }
					</uo-button>
				` );
			}


			// Check if the field is hidden
			if ( attributes.isHidden ) {
				// Add class to hide the field container
				fieldContainer.addClass( 'form-element--hidden' );
			}

			// Append everything to the form element container
			formElement.append( fieldContainer );

			// Check if it's a select and has a customField defined,
			// if so, add it
			if ( attributes.fieldType == 'select' && isDefined( this.customValueField ) ) {
				// Create container for the custom value field
				this.customValueFieldContainer = $(
					`<div class="form-select-custom">
						<div class="form-element-children"></div>
					</div>`
				);

				// Get the form element
				this.customValueFieldContainer
					.find( '.form-element-children' )
					.append( this.customValueField.createFormElement() );

				// Append it
				formElement.append( this.customValueFieldContainer );

				// Check if it's a multiselect
				if ( attributes.supportsMultipleValues ) {
					// Add class to the select
					fieldContainer.addClass( 'form-multi-select-custom' );
				}

				// Check if we have to show the custom field
				if ( isCustomValue( this.getValue() ) ) {
					this.customValueFieldContainer.addClass( 'form-select-custom--visible' );
				} else {
					this.customValueFieldContainer.removeClass( 'form-select-custom--visible' );
				}
			}

			this.field.get( 0 ).fieldInstance = this;

			// Response
			response = formElement;

			// Save Form Element
			this.formElement = formElement;

			// Init libraries
			if ( initLibraries ) {
				this.initLibraries();
			}

			// Listen fields for AJAX
			this.handleAJAX();

			// Trigger methods on change
			this.onChange();

			// Trigger methods on focus
			this.onFocus();
		} else {
			// Error: Invalid field type
			console.error( `The field type "${ type }" is invalid. The valid ones are ${ validFieldTypes.join( ', ' ) }` );
		}

		return response;
	}

	/**
	 * Set attributes. It add properties like hasValue, hasPlaceholder, etc, automatically
	 *
	 * @since 0.4
	 * @param {object} attributes - The field attributes
	 * @return {object} attributes - The field attributes
	 */

	setAttributes( attributes ) {
		// Save field type in the attributes
		attributes.fieldType = this.type;

		// Create an unique ID
		attributes.uniqueFieldID = `${ attributes.optionCode }__${ getUniqueID() }`;

		// Check if it's a checkbox and sanitaze the current value
		if ( attributes.fieldType == 'checkbox' && ! isEmpty( attributes.currentValue ) ) {
			// Check if the value is "true" or "false"
			if ( [ 'true', 'false' ].includes( attributes.currentValue ) ) {
				// Make it a boolean
				attributes.currentValue = attributes.currentValue == 'true' ? true : false;

				// If it's false, set hasValue to false
				if ( ! attributes.currentValue ) {
					attributes.hasValue = false;
				} else {
					attributes.hasValue = true;
				}
			}
		}

		// Has value
		if ( ! isDefined( attributes.hasValue ) ) {
			if ( ! isEmpty( attributes.currentValue ) ) {
				attributes.hasValue = true;
			} else {
				attributes.hasValue = false;
			}
		}

		// Default placeholder
		if (
			isEmpty( attributes.placeholder ) &&
			this.type === 'select' &&
			isDefined( attributes.ajax ) &&
			isDefined( attributes.ajax.event ) &&
			attributes.ajax.event === 'search_options'
		) {
			attributes.placeholder = escapeHTML( __( 'Search...', 'uncanny-automator' ) );
		}

		// Has placeholder
		if ( ! isDefined( attributes.hasPlaceholder ) ) {
			if ( ! isEmpty( attributes.placeholder ) ) {
				attributes.hasPlaceholder = true;
			} else {
				attributes.hasPlaceholder = false;
			}
		}

		// Has default value
		if ( ! isDefined( attributes.hasDefaultValue ) ) {
			if ( ! isEmpty( attributes.defaultValue ) ) {
				attributes.hasDefaultValue = true;
			} else {
				attributes.hasDefaultValue = false;
			}
		}

		// Has label
		if ( ! isDefined( attributes.hasLabel ) ) {
			if ( ! isEmpty( attributes.label ) ) {
				attributes.hasLabel = true;
			} else {
				attributes.hasLabel = false;
			}
		}

		// Check if we have to show the label of the field in the trigger/action sentence
		// This doesn't define whether the value of the field will be shown in the sentence. It defines whether —in case
		// we're showing the value of the field in the sentence— we'll show the label before the value.
		if ( ! isDefined( attributes.showLabelInSentence ) ) {
			attributes.showLabelInSentence = true;
		}

		// Has description
		if ( ! isDefined( attributes.hasDescription ) ) {
			if ( ! isEmpty( attributes.description ) ) {
				attributes.hasDescription = true;
			} else {
				attributes.hasDescription = false;
			}
		}

		// Default token name
		if ( isEmpty( attributes.tokenName ) ) {
			attributes.tokenName = attributes.label;
		}

		// Default copy to clipboard
		if ( ! isDefined( attributes.supportsCopyToClipboard ) ) {
			attributes.supportsCopyToClipboard = false;
		}

		// Is Required
		if ( ! isDefined( attributes.isRequired ) ) {
			attributes.isRequired = false;
		}

		// Is children
		if ( ! isDefined( attributes.isChildField ) ) {
			attributes.isChildField = false;
		}

		// Check default value for readOnly
		if ( ! isDefined( attributes.isReadOnly ) ) {
			attributes.isReadOnly = false;
		}

		// Check if it supports multiple values
		if ( ! isDefined( attributes.supportsMultipleValues ) ) {
			attributes.supportsMultipleValues = false;
		}

		if ( this.type === 'file' && ! isDefined( attributes.fileTypes ) ) {
			attributes.fileTypes = [];
		}

		// Check if it's a select
		if ( attributes.fieldType == 'select' ) {

			// ⚠️ Check if it supports multiple values, if it does,
			// don't support custom values by default unless it's defined
			if ( attributes.supportsMultipleValues ) {
				if ( ! isDefined( attributes.supportsCustomValue ) ) {
					attributes.supportsCustomValue = false;
				}
			} else {
				if ( ! isDefined( attributes.supportsCustomValue ) ) {
					if ( [ 'condition', 'action', 'closure', 'loop-filter' ].includes( attributes.itemType ) ) {
						attributes.supportsCustomValue = true;
					} else {
						attributes.supportsCustomValue = false;
					}
				}
			}

			// ⚠️ Check if the current dropdown is a multiselect,
			if ( attributes.supportsMultipleValues ) {
				// Try to parse the current value as JSON.
				try {
					attributes.currentValue = JSON.parse( attributes.currentValue );
				} catch ( e ) {
					// Continue regardless of error

					// Output error if the developer mode is enabled
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.warn( e );
					}
				}
			}

			// Check if the user defined a description for the custom field too
			attributes.hasCustomValueDescription = ! isEmpty( attributes.customValueDescription );
		}

		// Check if it's a select or radio
		if ( [ 'radio', 'select' ].includes( attributes.fieldType ) ) {
			if ( ! isDefined( attributes.options ) ) {
				attributes.options = [];
			}

			// Show the IDs by default
			if ( ! isDefined( attributes.optionsShowID ) ) {
				attributes.optionsShowID = true;
			}
		}

		// Check if it's an integer or float field
		if ( [ 'int', 'float' ].includes( attributes.fieldType ) ) {
			if ( ! isEmpty( attributes.minNumber ) ) {
				// Make sure it's a valid number
				const minNumber = parseFloat( attributes.minNumber );
				if ( ! isNaN( minNumber ) ) {
					attributes.minNumber = minNumber;
				}
			}

			if ( ! isEmpty( attributes.maxNumber ) ) {
				// Make sure it's a valid number
				const maxNumber = parseFloat( attributes.maxNumber );
				if ( ! isNaN( maxNumber ) ) {
					attributes.maxNumber = maxNumber;
				}
			}
		}

		// Check if it's a date field
		if ( attributes.fieldType == 'date' ) {
			attributes.isReadOnly = true;

			// Check if it doesn't have a custom date format
			if ( ! isDefined( attributes.dateFormat ) ) {
				attributes.dateFormat = 'Y-m-d';
			}

			// Check if it doesn't have a custom date alt format
			if ( ! isDefined( attributes.dateAltFormat ) ) {
				attributes.dateAltFormat = UncannyAutomator._site.date_format;
			}

			// Check if it has a min date
			if ( isDefined( attributes.minDate ) ) {
				attributes.minDate = attributes.minDate;
			}

			// Check if it has a max date
			if ( isDefined( attributes.maxDate ) ) {
				attributes.maxDate = attributes.maxDate;
			}

			// Set default value for the supports range option
			if ( ! isDefined( attributes.supportsRange ) ) {
				attributes.supportsRange = false;
			}
		}

		if ( ! isDefined( attributes.clearWhenAddingToken ) ) {
			attributes.clearWhenAddingToken = false;
		}

		// Check if it's a time field
		if ( attributes.fieldType == 'time' ) {
			attributes.isReadOnly = true;

			// Check if it doesn't have a custom time format
			if ( ! isDefined( attributes.timeFormat ) ) {
				attributes.timeFormat = 'h:i K'; // PHP: h:i A
			}

			// Check if it doesn't have a custom time alt format
			if ( ! isDefined( attributes.timeAltFormat ) ) {
				// WordPress format
				attributes.timeAltFormat = UncannyAutomator._site.time_format;
			}

			// Convert from PHP date format to the format user by flatpickr
			// Keys are the PHP format, values are the flatpickr format
			const flatpickrDateReplace = {
				// Lowercase Ante meridiem and Post meridiem
				a: 'K',
				// Uppercase Ante meridiem and Post meridiem
				A: 'K',
				// Swatch Internet time
				B: '',
				// 24-hour format of an hour without leading zeros
				G: 'H',
				// 12-hour format of an hour without leading zeros
				g: 'h',
				// 12-hour format of an hour with leading zeros
				h: 'g'
			}

			// Convert the PHP time format to flatpickr format
			try {
				attributes.timeAltFormat = attributes.timeAltFormat.replace(
					new RegExp( Object.keys( flatpickrDateReplace ).join( '|' ), 'g' ),
					( match ) => {
						return flatpickrDateReplace[ match ];
					}
				);
			} catch ( e ) {
				// Set another time format
				attributes.timeAltFormat = attributes.timeFormat;

				// Check if debugging mode is enabled
				// eslint-disable-next-line no-lonely-if
				if ( UncannyAutomatorBackend.debugging.enabled ) {
					// eslint-disable-next-line no-console
					console.warn( e );
				}
			}

			// Check if it has a min time
			if ( isDefined( attributes.minTime ) ) {
				attributes.minTime = attributes.minTime;
			}

			// Check if it has a max time
			if ( isDefined( attributes.maxTime ) ) {
				attributes.maxTime = attributes.maxTime;
			}
		}

		// Supports markdown
		if ( ! isDefined( attributes.supportsMarkdown ) ) {
			attributes.supportsMarkdown = false;
		}

		// Supports tinyMCE
		if ( ! isDefined( attributes.supportsTinyMCE ) ) {
			attributes.supportsTinyMCE = false;
		}

		// If it supports TinyMCE, set default values
		if ( attributes.supportsTinyMCE ) {
			// Full page editing
			if ( ! isDefined( attributes.supportsFullPageEditing ) ) {
				attributes.supportsFullPageEditing = false;
			}
			
			// Allowed HTML tags array for restricted mode
			if ( ! isDefined( attributes.allowedHtmlTags ) ) {
				attributes.allowedHtmlTags = null;
			}
			
			// Media library button
			if ( ! isDefined( attributes.supportsMedia ) ) {
				attributes.supportsMedia = true;
			}
		}

		// Is Toggle
		if ( ! isDefined( attributes.isToggle ) && isDefined( attributes.toggle ) ) {
			attributes.isToggle = true;
		}

		// Is AJAX
		if ( ! isDefined( attributes.isAjax ) ) {
			attributes.isAjax = false;
		}

		if (
			isDefined( attributes.ajax ) &&
			isDefined( attributes.ajax.event )
		) {
			attributes.ajax.firstLoadDone = false;
		}

		// Set the default value for isHidden
		if ( ! isDefined( attributes.isHidden ) ) {
			attributes.isHidden = false;
		}

		// Check if we should remove some tokens
		if ( ! isDefined( attributes.unsupportedTokens ) ) {
			attributes.unsupportedTokens = [];
		}

		// Check if we should show only LoopableTokens
		if ( ! isDefined( attributes.onlyLoopableTokens ) ) {
			attributes.onlyLoopableTokens = false;
		}

		// Check if the field is inside a trigger
		// If it is, disable tokens
		if ( attributes.itemType == 'trigger' ) {
			attributes.supportsTokens = false;
		}

		// Supports tokens
		if ( ! isDefined( attributes.supportsTokens ) ) {

			if (
				[ 'text', 'textarea', 'int', 'float', 'email', 'url', 'select', 'date', 'time' ].includes( attributes.fieldType ) &&
				[ 'condition', 'action', 'closure', 'loop-filter' ].includes( attributes.itemType ) &&
				! attributes.isReadOnly
			) {
				attributes.supportsTokens = true;
			} else {
				attributes.supportsTokens = false;
			}
		}

		// Check if it can support tokens
		if (
			attributes.supportsTokens &&
			attributes.fieldType == 'select' &&
			! [ 'condition', 'action', 'closure', 'loop-filter' ].includes( attributes.itemType )
		) {
			attributes.supportsTokens = false;
		}

		return attributes;
	}

	/**
	 * Gets the data needed by the server when doing AJAX requests from the field
	 *
	 * @param {Object} options
	 * @param {String} options.fieldToListenToCode The ID of the field that triggered the AJAX call
	 * @return {Object} The data to be sent to the server when doing an AJAX call made by the field
	 */
	getAjaxRequestData( { fieldToListenToCode } ) {
		return {
			recipe_id: UncannyAutomator._recipe.recipe_id,
			parent_id: this.attributes.parentID,
			item_id: this.attributes.itemId,
			group_id: this.attributes.groupId,
			field_id: this.attributes.optionCode,
			triggered_by: fieldToListenToCode,
			values: getUnsavedFieldsValues( {
				itemType: this.attributes.itemType,
				itemID: this.attributes.itemId,
				field: this
			} ),
		}
	}

	/**
	 * Handles the AJAX functionality on fields
	 * Fields will listen to changes on other fields and trigger an AJAX call to retrieve a value or dropdown options
	 */
	handleAJAX() {
		// Check if we should listen to other fields
		if ( ! isDefined( this.attributes.ajax ) || ! isDefined( this.attributes.ajax.event ) ) {
			return;
		}

		/**
		 * Performs the AJAX call
		 */
		const doAJAX = ( { fieldToListenToCode = '', onSuccess = () => {}, extraAttributes = {} } ) => {
			// Get the value of all the fields
			const fieldValues = getUnsavedFieldsValues( {
				itemType: this.attributes.itemType,
				itemID: this.attributes.itemId,
				field: this
			} );

			// Combines the data from an AJAX request with additional attributes.
			const ajaxValues = {
				...this.getAjaxRequestData( { fieldToListenToCode } ),
				...extraAttributes
			};

			// Before starting, clear any errors
			// This will hide errors from previous calls before doing the new one
			this.clearError();

			AJAXRequest(
				this.attributes.ajax.endpoint,
				ajaxValues,
				( response ) => {
					// We'll update this even if the call returns an error. The call itself was successful
					this.attributes.ajax.firstLoadDone = true;

					try {
						onSuccess();
					} catch ( e ) {}

					// Check if the call was successful
					if ( isDefined( response.success ) && ! response.success ) {
						// Check if there is a message available
						if ( ! isEmpty( response.error ) ) {
							this.setError( response.error );
						}

						// Set the new options
						if ( this.type === 'select' ) {
							this.setSelectOptions( [], false );
						}

						this.setValue( '' );

						return;
					}

					// Check if we have to set the select options
					if ( isDefined( response.options ) ) {
						// Set the new options
						this.setSelectOptions( response.options, false );
					}

					// Check if we have to set the value
					if ( isDefined( response.value ) ) {
						this.setValue( response.value );
					} else {
						// Check if this is a loop-filter or condition
						if ( [ 'loop-filter', 'condition' ].includes( this.attributes.itemType ) ) {
							if (
								isDefined( fieldValues[ this.attributes.optionCode ] ) &&
								isDefined( fieldValues[ this.attributes.optionCode ].value ) &&
								isCustomValue( fieldValues[ this.attributes.optionCode ].value )
							) {
								this.setValue( fieldValues[ `${ this.attributes.optionCode }` ].custom );
							} else {
								if (
									// Check if it's a multiselect
									this.attributes.supportsMultipleValues ||
									// Or if it's a single select, but the option exists
									(
										isDefined( fieldValues[ this.attributes.optionCode ] ) &&
										this.field.get( 0 ).querySelector( `option[value="${ fieldValues[ this.attributes.optionCode ].value }"]` )
									)
								) {
									this.setValue( fieldValues[ this.attributes.optionCode ].value );
								} else {
									// Check if the option exists
									this.setValue( '' );
								}
							}
						} else {
							// Check if it's a custom value
							if ( isCustomValue( fieldValues[ this.attributes.optionCode ] ) ) {
								this.setValue( fieldValues[ `${ this.attributes.optionCode }_custom` ] );
							} else {
								if (
									// Check if it's a multiselect
									this.attributes.supportsMultipleValues ||
									// Or if it's a single select, but the option exists
									this.field.get( 0 ).querySelector( `option[value="${ fieldValues[ this.attributes.optionCode ] }"]` )
								) {
									this.setValue( fieldValues[ this.attributes.optionCode ] );
								} else {
									// Check if the option exists
									this.setValue( '' );
								}
							}
						}
					}
				}
			);
		}

		// Check if we have to listen other fields
		if ( this.attributes.ajax.event === 'parent_fields_change' ) {
			// Check if the fields exist
			if ( ! isDefined( this.attributes.ajax.listenFields ) && Array.isArray( this.attributes.ajax.listenFields ) ) {
				return;
			}

			this.attributes.ajax.listenFields.forEach( ( fieldID ) => {
				// Get field
				const fieldToListenTo = getField( this.attributes.itemId, fieldID, this.attributes.itemType );

				// Get the debounce delay based on the field type
				const debounceDelay = [ 'date', 'time', 'select', 'radio', 'checkbox', 'file' ].includes( fieldToListenTo.type ) ? 0 : 1000;

				// Listen changes to the parent field
				fieldToListenTo.formElement.on( 'automator:field:change', debounce( ( event, context ) => {
					// Don't trigger when getting formElement
					if ( isDefined( context ) && isDefined( context.source ) && context.source === 'getFormElement' ) {
						return;
					}

					// Before doing the request, show a loading animation in the field that changed
					fieldToListenTo.startLoadingAnimation();
					// And show animation in the target field too
					this.startLoadingAnimation();

					doAJAX( {
						extraAttributes: {
							context: 'parent-field-changed'
						},
						fieldToListenToCode: fieldToListenTo.attributes.optionCode,
						onSuccess: () => {
							// Stop loading animation in both fields
							fieldToListenTo.stopLoadingAnimation();
							this.stopLoadingAnimation();
						}
					} );
				}, debounceDelay ) );

				// Check if it's a dropdown that supports custom values
				if ( fieldToListenTo.type === 'select' && fieldToListenTo.attributes.supportsCustomValue ) {
					fieldToListenTo.customValueField.formElement.on( 'automator:field:change', debounce( ( event, context ) => {
						// Don't trigger when getting formElement
						if ( isDefined( context ) && isDefined( context.source ) && context.source === 'getFormElement' ) {
							return;
						}

						// Before doing the request, show a loading animation in the field that changed
						fieldToListenTo.startLoadingAnimation();
						// Show loading animation in the custom value too
						fieldToListenTo.customValueField.startLoadingAnimation();
						// And show animation in the target field too
						this.startLoadingAnimation();

						doAJAX( {
							extraAttributes: {
								context: 'parent-field-changed'
							},
							fieldToListenToCode: fieldToListenTo.attributes.optionCode,
							onSuccess: () => {
								// Stop loading animation in all fields
								fieldToListenTo.stopLoadingAnimation();
								fieldToListenTo.customValueField.stopLoadingAnimation();
								this.stopLoadingAnimation();
							}
						} );
					}, 1000 ) );
				}

				// Trigger change event in the parent field as the field load
				// But do it only if the parent field isn't listening to another field too
				if ( ! isDefined( fieldToListenTo.attributes.ajax ) ) {
					fieldToListenTo.field.trigger( 'automator:field:change' );
				}
			} );

			// Check if the "Refresh" button exists and add event listener
			if ( this.$ajaxRefreshBtn ) {
				this.$ajaxRefreshBtn.addEventListener( 'click', () => {
					// Show animation in the target field
					this.startLoadingAnimation();

					doAJAX( {
						extraAttributes: {
							context: 'refresh-button'
						},
						fieldToListenToCode: this.attributes.optionCode,
						onSuccess: () => {
							// Stop loading animation
							this.stopLoadingAnimation();
						}
					} );
				} );
			}
		}

		// Check if we have to perform the call on load
		if ( this.attributes.ajax.event === 'on_load' ) {
			// Show animation in the target field
			this.startLoadingAnimation();

			doAJAX( {
				extraAttributes: {
					context: 'on-load'
				},
				fieldToListenToCode: this.attributes.optionCode,
				onSuccess: () => {
					// Stop loading animation
					this.stopLoadingAnimation();
				}
			} );

			// Check if the "Refresh" button exists and add event listener
			if ( this.$ajaxRefreshBtn ) {
				this.$ajaxRefreshBtn.addEventListener( 'click', () => {
					// Show animation in the target field
					this.startLoadingAnimation();

					doAJAX( {
						extraAttributes: {
							context: 'refresh-button'
						},
						fieldToListenToCode: this.attributes.optionCode,
						onSuccess: () => {
							// Stop loading animation
							this.stopLoadingAnimation();
						}
					} );
				} );
			}
		}

		// Check if we have to clear the field when another field changes
		if ( this.attributes.ajax.event === 'search_options' && isDefined( this.attributes.ajax.clearOnChangeFields ) && Array.isArray( this.attributes.ajax.clearOnChangeFields ) ) {
			this.attributes.ajax.clearOnChangeFields.forEach( ( fieldID ) => {
				// Get field
				const fieldToListenTo = getField( this.attributes.itemId, fieldID, this.attributes.itemType );

				// Get the debounce delay based on the field type
				const debounceDelay = [ 'date', 'time', 'select', 'radio', 'file', 'checkbox' ].includes( fieldToListenTo.type ) ? 0 : 1000;

				// Listen changes to the parent field
				fieldToListenTo.formElement.on( 'automator:field:change', debounce( ( event, context ) => {
					this.setValue( '' );
				}, debounceDelay ) );

				// Check if it's a dropdown that supports custom values
				if ( fieldToListenTo.type === 'select' && fieldToListenTo.attributes.supportsCustomValue ) {
					fieldToListenTo.customValueField.formElement.on( 'automator:field:change', debounce( ( event, context ) => {
						this.setValue( '' );
					}, 1000 ) );
				}
			} );
		}
	}

	/**
	 * Update the field type and updates the attributes
	 *
	 * @beta This method is in beta and might change in the future
	 */
	updateAttributes( type, newAttributes = {} ) {
		// Update the field type
		this.type = type;

		const updatedAttributes = {
			...this.attributes,
			...newAttributes,
			...{
				fieldType: type
			}
		};

		// Get the old form element
		const $oldFormElement = this.getFormElement();

		// Update the attributes
		this.attributes = this.setAttributes( updatedAttributes );

		// Create the form element again
		this.createFormElement();

		// Render the field again, if needed
		$oldFormElement.replaceWith( this.formElement );

		// Init libraries
		this.initLibraries();
		// Initilize the token selector
		this.initTokenSelector();
		// Initilize flatpickr
		this.initFlatpickr();
		// Initilize CodeMirror
		this.initCodeMirror();
	}

	/**
	 * Finds the label for a multiselect option.
	 *
	 * @param {String} label - The JSON string of labels. Each label is a string of the format 'value::label'.
	 * @param {String} currentValue - The value of the option for which to find the label.
	 *
	 * @returns {String} - The label corresponding to the currentValue if found, otherwise the original label.
	 */
	_helperFindMultiselectLabel( label, currentValue ) {
		const groupLabel = JSON.parse( label )
			.map( labelGroup => labelGroup.split( '::' ) )
			.find( labelGroup => labelGroup[0] == currentValue );

		if ( groupLabel ) {
			label = groupLabel[1];
		}

		return label;
	}

	/**
	 * Validate field
	 *
	 * @since 0.4
	 * @param {Boolean} renderError - TRUE if you want to render the field errors, FALSE if you only want to return the response object
	 * @return {validateField} validateField object. Read the comments on Validation.js to learn more
	 */

	validate( renderError = true ) {
		// Parameters

		// Check and apply dynamic visibility rules
		if ( this.attributes.dynamic_visibility ) {
			this.applyDynamicVisibility();
		}

		// Field (DOM)
		let field = null,
			value;

		if ( renderError ) {
			field = this.getFieldContainer();
			value = this.getValue();
		} else {
			value = this.attributes.currentValue;
		}

		// Type
		const fieldType = this.type;

		// Attributes
		const attributes = {
			supportsTokens: this.attributes.supportsTokens,
			required: this.attributes.isRequired,
			isHidden: this.attributes.isHidden,
		};

		// Check if it's a textarea, and check if it supports full-page editing
		if ( this.type === 'textarea' ) {
			attributes.supportsFullPageEditing = this.attributes.supportsFullPageEditing;
		}

		// Check if it's an integer or float field and we have to validate max and min numbers
		if ( [ 'int', 'float' ].includes( this.type ) ) {
			if ( isDefined( this.attributes.minNumber ) ) {
				attributes.minNumber = this.attributes.minNumber;
			}

			if ( isDefined( this.attributes.maxNumber ) ) {
				attributes.maxNumber = this.attributes.maxNumber;
			}
		}

		// This uses the validateField function. Check Validation.js for more information
		return validateField( field, this.type, value, attributes, this.customValidationFunction );
	}

	/**
	 * Shows an error in the field
	 *
	 * @param {String} message The message to clear the error
	 */
	setError( message = '' ) {
		// Make sure there is a message defined
		if ( isEmpty( message ) ) {
			return;
		}

		// Check if the field is defined
		if ( ! this.field ) {
			return;
		}

		// Add error classes
		this.$errorWrapper.innerHTML = message;

		// Add error class to the main wrapper
		this.formElement.get( 0 ).classList.add( 'form-element--error' );
	}

	/**
	 * Removes the error from a field
	 */
	clearError() {
		// Check if the field is defined
		if ( ! this.field ) {
			return;
		}

		// Remove message
		this.$errorWrapper.innerHTML = '';

		// Remove error class (hide error container too)
		this.formElement.get( 0 ).classList.remove( 'form-element--error' );
	}

	/**
	 * Returns the container that shows the error message
	 */
	get $errorWrapper() {
		return this.formElement.get( 0 ).querySelector( '.form-element__error' );
	}

	/**
	 * Invoke functions on field change
	 * Those functions are global for all the Field objects
	 * Don't use directly, use 'automator:field:change' instead
	 */
	onChange() {
		this.formElement.on( 'automator:field:change', ( event, customData ) => {
			// Check if it's a select that supports custom values (and that option is selected)
			// console.log( this.attributes.label );
			// console.log( '> 🚨 Change (automator:field:change)' );

			// Check if it has a value
			if ( isDefined( this.fieldContainer ) ) {
				if ( ! isEmpty( this.getValue() ) ) {
					this.fieldContainer.addClass( 'form-input--has-value' );
				} else {
					this.fieldContainer.removeClass( 'form-input--has-value' );
				}
			}

			if ( this.attributes.fieldType == 'select' && this.attributes.supportsCustomValue ) {
				const isMultiple = this.attributes.supportsMultipleValues;
				let resetMultiSelect = false;
				let selectValue = this.getValue();
				let selectIsCustom = isCustomValue( selectValue );
				if ( isMultiple ) {
					// Remove all other values and update the select again.
					if ( selectIsCustom && selectValue.length > 1 ) {
						// Remove all other values
						selectValue = ['automator_custom_value'];
						// Reset the multi-select
						resetMultiSelect = true;
					}
				}

				if ( selectIsCustom ) {
					// Show custom field container
					this.customValueFieldContainer.addClass( 'form-select-custom--visible' );

					// Make the multi-select field read-only
					if ( isMultiple ) {
						// Disable the field if required.
						if ( ! this.field.prop( 'disabled' ) ) {
							this.field.prop( 'disabled', true );
						}
						// Add multi select custom class to the field container.
						this.fieldContainer.addClass( 'form-multi-select-custom--visible' );
						// Remove any existing event listener for the clear button to avoid multiple click events.
						this.fieldContainer.off('click', '.select2-selection__clear');
						// Add event listener for the clear button.
						this.fieldContainer.on('click', '.select2-selection__clear', () => {
							this.field.prop('disabled', false).val(null).trigger('change');
						});
						// Limit selected value to the custom value.
						if ( resetMultiSelect ) {
							setTimeout( () => {
								this.setValue( ['automator_custom_value'], true );
							}, 0 );
						}
					}

				} else {
					// Hide custom field container
					this.customValueFieldContainer.removeClass( 'form-select-custom--visible' );
					if ( isMultiple ) {
						// Enable the field if required.
						if ( this.field.prop( 'disabled' ) ) {
							this.field.prop( 'disabled', false );
						}
						// Remove multi select custom class from the field container.
						this.fieldContainer.removeClass( 'form-multi-select-custom--visible' );
					}
				}
			}

			const onChangeAJAXCallback = () => {
				// Check if it's an AJAX field
				if ( this.attributes.isAjax ) {
					// Check if the target field is different
					if ( this.attributes.targetField !== this.attributes.optionCode ) {
						// console.log( this.attributes.label );
						// console.log( '> AJAX (onChangeAJAXCallback)' );

						this.doAJAX();
					}
				}

				// Check if it's a child field
				if ( isDefined( this.attributes.isChildField ) && this.attributes.isChildField ) {
					// Check if the option code of the parent is defined
					if ( isDefined( this.attributes.parentOptionCode ) ) {
						// Get the field
						const parentField = getField( this.attributes.itemId, this.attributes.parentOptionCode, this.attributes.itemType );

						// Check if we found it
						if ( isDefined( parentField ) ) {
							// console.log( this.attributes.label + ' --> ' + parentField.attributes.label );
							// console.log( '> AJAX (onChangeAJAXCallback)' );

							// Do AJAX call on parent
							parentField.doAJAX();
						}
					}
				}
			};

			// Check if it's an AJAX field, has a target field
			// and the target field is different than the option code
			if (
				// Check if the field is ajax
				this.attributes.isAjax &&
				// Check if there is a valid target field (Field instance)
				isDefined( this.attributes.targetField ) &&
				// Check if the ID of the target field is different that the
				// ID of the parent field
				this.attributes.targetField !== this.attributes.optionCode &&
				// Check if the parent field completed the first load
				( this.attributes.completedFirstLoad ||
					// Otherwise, even if the first load wasn't completed,
					// check if the "current value" (value saved) on
					// the database is empty, or if it doesn't have a default value
					( isEmpty( this.attributes.currentValue ) &&
						! this.attributes.hasDefaultValue &&
						! this.attributes.completedFirstLoad ) )
			) {
				// Get the target field
				const targetField = getField( this.attributes.itemId, this.attributes.targetField, this.attributes.itemType );

				// console.log( 'Reset value of ' + targetField.attributes.label );

				// Reset the value of the target field
				if ( ! targetField.attributes.supportsMultipleValues ) {
					targetField.setValue( null, false );
					targetField.setCustomValue( null, false );
				}
			}

			// Check if it's a dropdown, checkbox or radio
			if ( [ 'select', 'radio', 'checkbox', 'file' ].includes( this.attributes.fieldType ) ) {
				// Do it right away
				onChangeAJAXCallback();
			} else {
				// Check if the inverval exists, and clear it
				if ( isDefined( this.onChangeTimeout ) ) {
					clearTimeout( this.onChangeTimeout );
				}

				// Wait a few seconds before doing the change
				this.onChangeTimeout = setTimeout( () => {
					onChangeAJAXCallback();
				}, 1500 );
			}

			// Return false to prevent bubbling in child fields
			return false;
		} );
	}

	/**
	 * Invoke functions on field change
	 * Those functions are global for all the Field objects
	 * Don't use directly, use 'automator:field:change' instead
	 */

	onFocus() {
		this.field.on( 'focusin', ( event ) => {
			// Check if it's a read-only field
			if ( this.attributes.isReadOnly ) {
				// Select the text
				$( event.currentTarget ).select();
			}
		} );

		this.field.on( 'focusout', () => {} );
	}

	/**
	 * Check if the item's object has all the required properties
	 */
	isReady() {
		return isDefined( UncannyAutomator.recipe.items[ this.attributes.itemId ] );
	}

	/**
	 * Do AJAX
	 *
	 * @since 0.5
	 */

	doAJAX() {
		// Get field value. If it isn't defined in the DOM then get the default value
		const fieldValue = isDefined( this.getValue() ) ? this.getValue() : this.getMetaValue();

		// Check if we have to do an AJAX call on this field
		if ( this.attributes.isAjax ) {
			// Get the target field
			let targetField = this;

			if ( this.attributes.optionCode != this.attributes.targetField ) {
				targetField = getField( this.attributes.itemId, this.attributes.targetField, this.attributes.itemType );
			}

			// Check that we have everything
			if ( ! isEmpty( this.attributes.integrationId ) && ! isEmpty( this.attributes.endpoint ) ) {
				// Before doing the request, show a loading animation in the changed field
				this.startLoadingAnimation();
				// And show animation in the target field too
				targetField.startLoadingAnimation();

				// Get parent field value
				let parentFieldValue = this.getValue(),
					targetFieldValue = isDefined( targetField.getValue() )
						? targetField.getValue()
						: targetField.getMetaValue();

				AJAXRequest(
					this.attributes.endpoint,
					{
						recipe_id: UncannyAutomator._recipe.recipe_id,
						integration_id: this.attributes.integrationId,
						item_id: this.attributes.itemId,
						group_id: this.attributes.groupId,
						field_id: this.attributes.optionCode,
						target_field: this.attributes.targetField,
						value: parentFieldValue,
						values: getUnsavedFieldsValues( {
							itemType: this.attributes.itemType,
							itemID: this.attributes.itemId,
							field: this
						} ),
					},
					( response ) => {
						// Stop loading animation in both fields
						this.stopLoadingAnimation();
						targetField.stopLoadingAnimation();

						const targetFieldAttributes = targetField.getAttributes();

						// console.table({
						// 	parentField:      this.getAttributes().optionCode,
						// 	parentFieldValue: parentFieldValue,
						// 	targetField:      this.attributes.targetField,
						// 	targetFieldValue: targetFieldValue
						// });

						// Check if the target field is a select and the response is an array

						// console.log( targetFieldAttributes.label );
						// console.log( '> Set options' );

						if ( targetFieldAttributes.fieldType == 'select' && Array.isArray( response ) ) {
							// Value of the target field before the call

							if (
								// Check if the field is ajax
								this.attributes.isAjax &&
								// Make sure it's not a multiselect
								( ! this.attributes.supportsMultipleValues ) &&
								// Check if there is a valid target field (Field instance)
								isDefined( this.attributes.targetField ) &&
								// Check if the ID of the target field is different that the
								// ID of the parent field
								this.attributes.targetField !== this.attributes.optionCode &&
								// Check if the parent field completed the first load
								( this.attributes.completedFirstLoad ||
									// Otherwise, even if the first load wasn't completed,
									// check if the "current value" (value saved) on
									// the database is empty, or if it doesn't have a default value
									( isEmpty( this.attributes.currentValue ) &&
										! this.attributes.hasDefaultValue &&
										! this.attributes.completedFirstLoad ) )
							) {
								targetFieldValue = null;
							}

							// Set the current value as the target field value if it's empty
							if ( ! this.attributes.completedFirstLoad && isEmpty( targetFieldValue ) ) {
								targetFieldValue = targetField.attributes.currentValue;
							}

							// Check if the value of the target field is custom
							if ( isCustomValue( targetFieldValue ) ) {
								// Get the custom field value
								const customFieldValue = targetField.getSelectCustomValue();

								// Check if the value of the custom field is NOT empty
								if ( ! isEmpty( customFieldValue ) ) {
									// Then use the custom value as the real value
									targetFieldValue = customFieldValue;
								} else {
									// Otherwise, don't use the custom value at all
									targetFieldValue = null;
								}
							}

							// console.log( targetField.attributes.label );
							// console.log( '> Value: ' + targetFieldValue );

							// The developer wants to set new options for a select
							// Don't trigger change (even though select2 needs it),
							// we're going to trigger it later
							targetField.setSelectOptions( response, false );

							// console.log( targetField.attributes.label + ' (by ' + this.attributes.label + ')' );
							// console.log( '> Set: ' + targetFieldValue );

							// Set the value again
							targetField.setValue( targetFieldValue );
						} else {
							// Try to set the value
							targetField.setValue( response );
						}

						// Update the "completed first load" status
						this.attributes.completedFirstLoad = true;
					}
				);
			}
		}
	}

	/**
	 * Get form element
	 *
	 * @since 0.4
	 * @return {object} jQuery object of the field's container
	 */

	getFormElement() {
		if ( [ 'trigger', 'action', 'closure' ].includes( this.attributes.itemType ) && this.isReady() ) {
			// Reset the "first load" status
			this.attributes.completedFirstLoad = false;

			if (
				isDefined( this.attributes.ajax ) &&
				isDefined( this.attributes.ajax.firstLoadDone )
			) {
				this.attributes.ajax.firstLoadDone = false;
			}

			// Do the AJAX call only if it's not a child field
			// Otherwise, we will just wait for the AJAX call
			// of the parent field to end. That will trigger an
			// AJAX call in the child field.
			let isTargetField = false;

			try {
				// Get group of options
				const fields =
					UncannyAutomator.recipe.items[ this.attributes.itemId ].options[ this.attributes.groupId ].fields;

				// Determine if it's a target field
				isTargetField = fields.reduce( ( accumulator, field ) => {
					return (
						accumulator ||
						// Check if this field is the target field of another field
						( field.attributes.targetField == this.attributes.optionCode &&
							// But it's not the field itself
							field.attributes.optionCode != this.attributes.optionCode &&
							// And it's AJAX
							field.attributes.isAjax )
					);
				}, false );
			} catch ( e ) {
				// Continue regardless of error

				// Output error if the developer mode is enabled
				if ( UncannyAutomator._site.has_debug_enabled ) {
					// eslint-disable-next-line no-console
					// console.warn( e );
				}
			}

			if ( ! isTargetField ) {
				// console.log( this.attributes.label );
				// console.log( '> AJAX (getFormElement)' );

				// Check if the field AJAX, and it's both the parent and target
				if ( this.attributes.targetField == this.attributes.optionCode && this.attributes.isAjax ) {
					this.doAJAX();
				}

				this.field.trigger( 'automator:field:change', {
					source: 'getFormElement',
					manuallySet: false,
				} );
			}
		}

		return this.formElement;
	}

	get $wrapper() {
		return this.getFormElement();
	}

	/**
	 * Get field container
	 *
	 * @return {object} jQuery object of the field's container
	 */
	getFieldContainer() {
		return this.fieldContainer;
	}

	/**
	 * Get field ID
	 *
	 * @since 0.5
	 * @return {String} The field ID
	 */

	getFieldID() {
		return this.attributes.optionCode;
	}

	/**
	 * Returns the field ID
	 * @return {String} The field ID
	 */
	get id() {
		return this.getFieldID();
	}

	get $ajaxRefreshBtn() {
		return this.formElement.get( 0 ).querySelector( '.form-element-ajax-refresh-btn' );
	}

	/**
	 * Get field
	 *
	 * @since 0.4
	 * @return {object} jQuery object of the field
	 */

	getField() {
		return this.field;
	}

	getFieldCM() {
		return this.$fakeCodemirrorField;
	}

	/**
	 * Get attributes
	 *
	 * @since 0.5
	 * @return {object} Field attributes
	 */

	getAttributes() {
		return this.attributes;
	}

	/**
	 * Get field's value FROM THE DOM ELEMENT
	 * If you want to get the value from the main object then check {attributes.currentValue}
	 *
	 * @since 0.4
	 * @return {String|int} Value of the field
	 */

	getValue() {
		let response = null;

		if ( isDefined( this.field ) ) {
			switch ( this.type ) {
				case 'select':
					if (
						// This means it's AJAX, and that the select doesn't have all its options yet
						// We must get the value from the meta
						isDefined( this.attributes.ajax ) &&
						! this.attributes.ajax.firstLoadDone
					) {
						response = this.valueFromMeta;
					} else {
						response = this.field.val();
					}

					break;

				case 'text':
				case 'url':
				case 'email':
				case 'int':
				case 'float':
					response = this.field.val();
					break;

				case 'textarea':
					// Check if it has a token selector
					if ( isDefined( this.TokenSelector ) ) {
						try {
							this.TokenSelector.parseHTMLTokensInPlainTextarea();
						} catch ( e ) {
							// Continue regardless of error

							// Output error if the developer mode is enabled
							if ( UncannyAutomator._site.has_debug_enabled ) {
								// eslint-disable-next-line no-console
								console.warn( e );
							}
						}
					}

					response = this.field.val();

					try {
						// Check if TinyMCE is enabled
						if ( isDefined( this.fieldTinyMCE ) && ! this.fieldTinyMCE.hidden ) {
							// Get the value from the TinyMCE field instead
							response = this.fieldTinyMCE.getContent( {
								format: 'raw',
							} );
						}
					} catch ( e ) {}

					break;

				case 'checkbox':
					response = this.field.is( ':checked' );
					break;

				case 'radio':
					response = this.field.find( 'input:checked' ).val();
					break;

				case 'date':
					response = this.field.val();
					break;

				case 'time':
					response = this.field.val();
					break;

				case 'file':
					response = this.field.get(0).value;
					break;
			}
		}

		if ( ! isDefined( response ) ) {
			response = this.valueFromMeta;
		}

		return response;
	}

	/**
	 * Returns the field value
	 *
	 * @return {String} The field value
	 */
	get value() {
		return this.getValue();
	}

	/**
	 * Returns the custom value
	 *
	 * @return {String} The custom value
	 */
	get customValue() {
		return this.customValueField.value;
	}

	/**
	 * Returns the value from the meta
	 */
	get valueFromMeta() {
		let value = null;

		// Check if the field has a value and add it
		if ( this.attributes.hasValue ) {
			// Return that value
			value = this.attributes.currentValue;
		} else if ( this.attributes.hasDefaultValue ) {
			// Return the default value
			value = this.attributes.defaultValue;
		}

		return value;
	}

	/**
	 * Returns the value of the field using the format needed by components created after 5.0
	 *
	 * @return {Object}
	 */
	get valueStandardFormat() {
		// Create the field data
		const fieldValue = {
			type: this.type,
			value: this.value
		}

		// Check if we have to add the readable value
		if ( this.type === 'select' ) {
			fieldValue.readable = this.readableValue;

			if ( isCustomValue( fieldValue.value ) && this.attributes.supportsCustomValue && this.customValueField ) {
				fieldValue.custom = this.customValue;
				fieldValue.readable = this.customValue;
			}
		}

		// Create backup. This will be used if the item is missing
		fieldValue.backup = {
			label: this.attributes.label,
			show_label_in_sentence: this.attributes.showLabelInSentence,
		}

		if ( this.type === 'select' ) {
			fieldValue.backup.supports_custom_value = this.attributes.supportsCustomValue;
			fieldValue.backup.supports_multiple_values = this.attributes.supportsMultipleValues;
		}

		if ( this.type === 'textarea' ) {
			fieldValue.backup.supports_tinymce = this.attributes.supportsTinyMCE;
			fieldValue.backup.supports_fullpage_editing = this.attributes.supportsFullPageEditing;
			fieldValue.backup.supports_markdown = this.attributes.supportsMarkdown;
			if ( this.attributes.supportsTinyMCE ) {
				fieldValue.backup.allowed_html_tags = this.attributes.allowedHtmlTags;
				fieldValue.backup.supports_media = this.attributes.supportsMedia;
			}
		}

		return fieldValue;
	}

	/**
	 * Get readable value
	 *
	 * @since 0.6
	 * @return string with the value
	 */
	getReadableValue() {
		let response = null,
			value = this.getValue();

		switch ( this.type ) {
			case 'select':
				// Check if the field DOM element is defined
				if ( isDefined( this.field ) ) {
					// Check if it supports multiple values, and the value is an array
					if ( this.attributes.supportsMultipleValues && Array.isArray( value ) ) {
						const optionsText = '';

						try {
							response = value
								.map( ( optionValue ) => {
									return this.getSelectOptionByValue( optionValue ).text;
								} )
								/* translators: Character to separate items */
								.join( `${ escapeHTML( __( ',', 'uncanny-automator' ) ) } ` );
						} catch ( e ) {
							response = JSON.stringify( value );
						}
					} else {
						response = this.field.find( `option[value="${ value }"]` ).text();
					}
				} else {
					// Check if it supports multiple values, and the value is an array
					if ( this.attributes.supportsMultipleValues && $.isArray( value ) ) {
						try {
							const optionsText = '';

							response = value
								.map( ( optionValue ) => {
									return this.getSelectOptionByValue( optionValue ).text;
								} )
								/* translators: Character to separate items */
								.join( `${ escapeHTML( __( ',', 'uncanny-automator' ) ) } ` );
						} catch ( e ) {
							// Continue regardless of error

							// Output error if the developer mode is enabled
							if ( UncannyAutomator._site.has_debug_enabled ) {
								// eslint-disable-next-line no-console
								console.warn( e );
							}
						}
					} else {
						try {
							// Get option from the options array
							response = this.getSelectOptionByValue( value ).text;
						} catch ( e ) {
							// Continue regardless of error

							// Output error if the developer mode is enabled
							if ( UncannyAutomator._site.has_debug_enabled ) {
								// eslint-disable-next-line no-console
								// console.warn( e );
							}
						}
					}
				}
				break;

			case 'text':
			case 'url':
			case 'email':
			case 'int':
			case 'float':
			case 'textarea':
				// Create DOM element to remove the HTML tags
				value = $( '<p/>' ).html( value ).text();
				break;

			case 'checkbox':
				if ( value ) {
					response = escapeHTML( __( 'True', 'uncanny-automator' ) );
				} else {
					response = escapeHTML( __( 'False', 'uncanny-automator' ) );
				}
				break;

			case 'radio':
				// Check if the field DOM element is defined
				if ( isDefined( this.field ) ) {
					// Get checked radio
					const $checkedRadio = this.field.find( 'input:checked' );

					// Check if there is a checked radio
					if ( $checkedRadio.length == 1 ) {
						// Get container
						const $radioContainer = $checkedRadio.closest( '.form-radio__container' );

						// Get text
						const radioText = $radioContainer.text();
						response = typeof radioText === 'string' ? radioText.trim() : '';
					}
				} else {
					try {
						// Get option from the options array
						response = this.getSelectOptionByValue( value ).text;
					} catch ( e ) {
						// Continue regardless of error

						// Output error if the developer mode is enabled
						if ( UncannyAutomator._site.has_debug_enabled ) {
							// eslint-disable-next-line no-console
							console.warn( e );
						}
					}
				}
				break;

			case 'date':
				// Create DOM element to remove the HTML tags
				value = $( '<p/>' ).html( value ).text();
				break;

			case 'time':
				// Create DOM element to remove the HTML tags
				value = $( '<p/>' ).html( value ).text();
				break;

			case 'file':
				const fileNames = value.map( attachment => {
					return attachment.title;
				} );

				// Create DOM element to remove the HTML tags
				response = fileNames.join( ', ' );
				break;
		}

		return response;
	}

	/**
	 * Returns the readable value
	 *
	 * @return {String} The readable value
	 */
	get readableValue() {
		return this.getReadableValue();
	}

	/**
	 * Get the custom value of a select (if there is one)
	 *
	 * @since 2.5
	 * @return string, with the value
	 */
	getSelectCustomValue() {
		// Default response
		let fieldValue = null;

		// Check if it's a select
		if (
			this.attributes.fieldType == 'select' &&
			this.attributes.supportsCustomValue &&
			isCustomValue( this.getValue() )
		) {
			// Check if the child field was created
			if ( isDefined( this.customValueField ) ) {
				fieldValue = this.customValueField.getValue();
			} else {
				// Try to get it from the meta
				try {
					fieldValue =
						UncannyAutomator.recipe.items[ this.attributes.itemId ].meta[
							`${ this.attributes.optionCode }_custom`
						];
				} catch ( e ) {
					// Continue regardless of error

					// Output error if the developer mode is enabled
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.warn( e );
					}
				}
			}
		}

		return fieldValue;
	}

	get selectCustomValue() {
		// Check if it's a select
		if ( ! this.type === 'select' ) {
			return;
		}

		return this.getSelectCustomValue();
	}

	/**
	 * Return the field label
	 */
	get label() {
		return this.attributes.label;
	}

	/**
	 * Get a select option by option value
	 *
	 * @param  value
	 * @since 2.5
	 * @return array
	 */
	getSelectOptionByValue( value = '' ) {
		let option = {
			text: value,
			value: value
		};

		const { options, supportsCustomValue } = this.attributes;

		// Check if it supports a custom value
		if ( supportsCustomValue ) {
			options.push( {
				value: 'automator_custom_value',
				text: escapeHTML( __( 'Use a token/custom value', 'uncanny-automator' ) ),
			} );
		}

		// Check if the options are defined
		if ( ! isEmpty( options ) ) {
			// Iterate options and find the value
			options.forEach( ( selectOption ) => {
				// Compare the option value with the value parameter
				if ( value == selectOption.value ) {
					option = selectOption;
				}
			} );
		}

		// Fallback until we change to web components
		if ( 
			isEmpty( options ) && 
			this.attributes.ajax?.firstLoadDone 
		) {
			const $optionElement = Array.from(
				this.field[0].querySelectorAll( 'option' ) 
			).filter( $optionElement => $optionElement.value == value );

			if ( $optionElement.length > 0 ) {
				option = {
					value: $optionElement[0].value,
					text: $optionElement[0].textContent,
				};
			}
		}

		return option;
	}

	/**
	 * Get field's meta value
	 *
	 * @since 0.5
	 * @return {string|int} Value of the field
	 */

	getMetaValue() {
		let response;

		// If the item is defined
		if ( isDefined( UncannyAutomator.recipe.items[ this.attributes.itemId ] ) ) {
			const metaValue =
				UncannyAutomator.recipe.items[ this.attributes.itemId ].meta[ this.attributes.optionCode ];

			if ( isDefined( metaValue ) ) {
				response = metaValue;
			}
		}

		return response;
	}

	/**
	 * Set value
	 *
	 * @since 0.4
	 * @param {String} Value of the field
	 */

	setValue( value, doTrigger = true, options = { updateCodemirror: true } ) {
		if ( isDefined( this.field ) ) {
			switch ( this.type ) {
				case 'select':
					// Sanitize the value
					if ( ! isDefined( value ) ) {
						if ( this.attributes.supportsMultipleValues ) {
							value = [];
						} else {
							value = '';
						}
					}

					// Get all options
					const allOptions = this.field.find( 'option' );

					if ( this.attributes.supportsMultipleValues ) {
						// Unselect all the options
						allOptions.prop( 'selected', false );

						// Iterate the selected options
						if ( $.isArray( value ) && ! isEmpty( value ) ) {
							// Iterate the values
							value.forEach( ( optionID ) => {
								// Get option
								const option = this.field.find( `option[value="${ optionID }"]` );

								// Check if option exists
								if ( option.length > 0 ) {
									// Select option
									option.prop( 'selected', true );
								}
							} );

							// Trigger change
							if ( doTrigger ) {
								this.field.trigger( 'change', {
									manuallySet: false,
								} );
							}
						}
					} else {
						// Get option
						const option = this.field.find( `option[value="${ value }"]` );

						// Check if option exists
						if ( option.length > 0 ) {
							// Unselect all the options
							allOptions.prop( 'selected', false );

							// Select option
							option.prop( 'selected', true );

							// Trigger change
							if ( doTrigger ) {
								this.field.trigger( 'change', {
									manuallySet: false,
								} );
							}
						} else {
							// Unselect all the options
							allOptions.prop( 'selected', false );

							// Check if the field supports custom options
							if ( this.attributes.supportsCustomValue && ! isEmpty( value ) ) {
								// Set the value as a custom option
								this.setCustomValue( value, false );
							}

							// Trigger change
							if ( doTrigger ) {
								this.field.trigger( 'change', {
									manuallySet: false,
								} );
							}
						}
					}

					break;

				case 'text':
				case 'url':
				case 'email':
				case 'int':
				case 'float':
					// Check if we should try to update the CodeMirror field
					if (
						isDefined( options.updateCodemirror ) &&
						options.updateCodemirror &&
						isDefined( this.fieldCM )
					) {
						// Try to set the value
						try {
							// console.log( 'Set the value in Codemirror' );
							// Set value using CodeMirror's method
							// this will copy the value in the real field
							this.fieldCM.setValue( value );
						} catch ( e ) {
							// console.log( 'Set the value in the real field FALLBACK' );
							// Fallback. Set the value in the real field
							this.field.val( value );
						}
					} else {
						// console.log( 'Set the value in the real field' );
						// Set value
						this.field.val( value );
					}

					// Trigger change
					if ( doTrigger ) {
						this.field.trigger( 'automator:field:change' );
					}
					break;

				case 'textarea':
					// Set the value
					this.field.val( value );

					// Set the value in the TinyMCE editor too
					if ( isDefined( this.fieldTinyMCE ) ) {
						this.fieldTinyMCE.setContent( value );
					}

					// Check if we should try to update the CodeMirror field
					if (
						isDefined( options.updateCodemirror ) &&
						options.updateCodemirror &&
						isDefined( this.fieldCM )
					) {
						// Try to set the value
						try {
							// console.log( 'Set the value in Codemirror' );
							// Set value using CodeMirror's method
							// this will copy the value in the real field
							this.fieldCM.setValue( value );
						} catch ( e ) {
							// console.log( 'Set the value in the real field FALLBACK' );
							// Fallback. Set the value in the real field
							this.field.val( value );
						}
					} else {
						// console.log( 'Set the value in the real field' );
						// Set value
						this.field.val( value );
					}

					/*
					// Check if "Text" view is selected
					if ( this.field.is( ':visible' ) ){

					}
					else {
						// Insert token
						this.editor.execCommand( 'mceInsertContent', false, token );
					}



					// Set value
					this.field.val( value );*/

					// Trigger change
					if ( doTrigger ) {
						this.field.trigger( 'automator:field:change' );
					}
					break;

				case 'checkbox':
					const wantsToCheck = value, // Just for readability
						isChecked = this.getValue();

					// If true, check
					if ( wantsToCheck && ! isChecked ) {
						this.field.prop( 'checked', true );
					}

					if ( ! wantsToCheck && isChecked ) {
						this.field.prop( 'checked', false );
					}

					if ( doTrigger && ( ( wantsToCheck && ! isChecked ) || ( ! wantsToCheck && isChecked ) ) ) {
						// Trigger change
						this.field.trigger( 'automator:field:change' );
					}

					break;

				case 'radio':
					// Search for the radio and check it
					this.field.filter( `[name="${ value }"]` ).prop( 'checked', true );

					// Trigger change
					if ( doTrigger ) {
						// Trigger change
						this.field.trigger( 'automator:field:change' );
					}

					break;

				case 'date':
					// Set value
					this.field.val( value );

					// Trigger change
					if ( doTrigger ) {
						this.field.trigger( 'automator:field:change' );
					}
					break;

				case 'time':
					// Set value
					this.field.val( value );

					// Trigger change
					if ( doTrigger ) {
						this.field.trigger( 'automator:field:change' );
					}
					break;
			}
		}
	}

	/**
	 * Clear the value
	 *
	 * @since 3.0
	 */

	clearValue() {
		// Set an empty value
		this.setValue( '', true );

		// Clear the value in the Codemirror instance
		try {
			this.fieldCM.setValue( '' );
		} catch ( e ) {
			// Continue regardless of error

			// Output error if the developer mode is enabled
			if ( UncannyAutomator._site.has_debug_enabled ) {
				// eslint-disable-next-line no-console
				console.warn( e );
			}
		}

		// Check if it's a date or time field
		if ( [ 'date', 'time' ].includes( this.attributes.fieldType ) ) {
			// Try to clear the flatpickr value
			try {
				this.flatpickrInstance.clear();
			} catch ( e ) {
				// Continue regardless of error

				// Output error if the developer mode is enabled
				if ( UncannyAutomator._site.has_debug_enabled ) {
					// eslint-disable-next-line no-console
					console.warn( e );
				}
			}
		}
	}

	/**
	 * Set value of the custom value
	 *
	 * @since 2.8.2
	 */

	setCustomValue( value, doTrigger = true ) {
		// console.log( this.attributes.label );
		// console.log( '> setCustomValue: ' + value );

		// Check if it's a select and has a customField defined,
		// if so, add it
		if ( this.attributes.fieldType == 'select' && isDefined( this.customValueField ) ) {
			// First, select the custom option
			this.field.val( 'automator_custom_value' );

			// Set the value in the custom value field
			this.customValueField.setValue( value, doTrigger );

			// Check if the user wants to trigger the change event
			if ( doTrigger ) {
				this.field.trigger( 'change', {
					manuallySet: false,
				} );
			}
		}
	}

	/**
	 * Check if a select has a selected option
	 *
	 * @since 0.5
	 */

	hasSelectedOption() {
		return this.selectHasSelectedOption;
	}

	/**
	 * Set select options
	 *
	 * @since 0.5
	 * @param {array}
	 */

	setSelectOptions( options, triggerChange = true ) {
		// Remove current options
		this.removeSelectOptions();

		// Sanitize the select options
		try {
			options = options.filter( ( option ) => {
				return isDefined( option.value ) && isDefined( option.text );
			} );
		} catch ( e ) {
			// Continue regardless of error

			// Output error if the developer mode is enabled
			if ( UncannyAutomator._site.has_debug_enabled ) {
				// eslint-disable-next-line no-console
				console.warn( e );
			}
		}

		if ( isDefined( this.field ) ) {
			// Get the container where we're going to add the options
			const $optionsContainer = this.field.find( '.form-element__select-normal-options' );

			if ( options.length > 0 ) {
				// Remove disabled
				this.field.prop( 'disabled', false );

				// Get value
				const value = isDefined( this.getValue() ) ? this.getValue() : this.getMetaValue();

				// Create array to save all options
				const optionsDOM = [];

				// Create and append options
				$.each( options, ( index, option ) => {
					const optionDOM = new Option( option.text, option.value, false, false );

					$optionsContainer.append( optionDOM );
					optionsDOM.push( optionDOM );
				} );

				if ( ! isEmpty( value ) ) {
					$.each( this.field.find( 'option' ), ( index, option ) => {
						if ( option.value == value ) {
							$( option ).prop( 'selected', true );
						}
					} );
				}
			} else {
				const $noResults = $(
					new Option(
						escapeHTML( __( 'No results found', 'uncanny-automator' ) ),
						'',
						! this.attributes.supportsCustomValue,
						! this.attributes.supportsCustomValue
					)
				);
				$noResults.prop( 'disabled', true );

				$optionsContainer.append( $noResults );

				if ( ! this.attributes.supportsCustomValue ) {
					// Disable the select
					this.field.prop( 'disabled', true );
				}
			}

			if ( triggerChange ) {
				this.field.trigger( 'change' );
				// this.field.trigger( 'automator:field:change' );
			}

			// Render this select2 again
			this.rerenderSelect2();
		}
	}

	/**
	 * Remove select options
	 *
	 * @since 0.5
	 */

	removeSelectOptions() {
		if ( isDefined( this.field ) ) {
			this.field.find( '.form-element__select-normal-options' ).empty();
		}
	}

	/**
	 * Check if field has loading animation
	 *
	 * @since 0.4
	 * @return {Boolean} TRUE if the field has the loading animation
	 */

	isLoading() {
		return this.formElement.hasClass( 'form-element--loading' );
	}

	/**
	 * Add loading animation to field
	 *
	 * @since 0.4
	 */

	startLoadingAnimation() {
		// Check if has class
		const CSSclass = 'form-element--loading';

		if ( isDefined( this.formElement ) && ! this.formElement.hasClass( CSSclass ) ) {
			// Add class
			this.formElement.addClass( CSSclass );

			// Check if it's a checkbox
			if ( this.type == 'checkbox' && ! isDefined( this.attributes.isToggle ) ) {
				this.fieldContainer.addClass( 'form-checkbox--loading-inline' );
			}

			// Trigger
			this.formElement.trigger( 'automator:field:loading' );
		}
	}

	/**
	 * Remove loading animation to field
	 *
	 * @since 0.4
	 */

	stopLoadingAnimation() {
		// Check if has class
		const CSSclass = 'form-element--loading';

		if ( isDefined( this.formElement ) && this.formElement.hasClass( CSSclass ) ) {
			// Remove class
			this.formElement.removeClass( CSSclass );

			// Remove checkbox class
			this.fieldContainer.removeClass( 'form-checkbox--loading-inline' );

			// Trigger
			this.formElement.trigger( 'automator:field:loading' );
		}
	}

	/**
	 * Init field libraries, like Select2
	 *
	 * @since 0.5
	 */

	initLibraries() {
		switch ( this.type ) {
			case 'select':
				// Select2
				this.initSelect2( this.field );
				break;

			case 'text':
			case 'url':
			case 'email':
			case 'int':
			case 'float':
			case 'textarea':
				break;

			case 'date':
			case 'time':
				break;
		}

		// Check and apply dynamic visibility rules
		if ( this.attributes.dynamic_visibility ) {
			this.applyDynamicVisibility();
			this.setupVisibilityChangeListener();
		}
	}

	/**
	 * Creates the object that select2 expects to render the options after receiving them from an AJAX call
	 * @param {Array} options The select options
	 */
	parseSelect2Response( options = [] ) {
		const groups = [];

		// Add the normal options
		const normalOptions = [];
		options.forEach( option => normalOptions.push( {
			id: isDefined( option.id ) ? option.id : option.value,
			text: option.text
		} ) );

		// Create the group of options
		groups.push( {
			text: '',
			children: normalOptions
		} );

		// Check if we should add the "Custom value" option
		if ( this.attributes.supportsCustomValue ) {
			groups.push( {
				text: escapeHTML( __( 'Other options', 'uncanny-automator' ) ),
				children: [ {
					id: 'automator_custom_value',
					text: escapeHTML( __( 'Use a token/custom value', 'uncanny-automator' ) )
				} ]
			} );
		}

		return {
			results: groups
		};
	}

	/**
	 * Select
	 * Get the select2 settings
	 *
	 * @since 2.7.3
	 */

	getSelect2Settings() {
		// Create function to render the options
		const renderHandler = ( data ) => {
			let template = data.text;

			// Check if the ID is defined
			if (
				// Make sure the ID is not empty
				! isEmpty( data.id ) &&
				// Check if we should show the ID
				this.attributes.optionsShowID &&
				// Make sure it's not the "Custom value" option
				( ! isCustomValue( data.id ) && data.id != '-1' )
			) {
				const optionID = `${ data.id }`;

				// Check if it's a long ID
				const hasLongID = optionID.length > 10;

				// Add ID
				template = `<span class="uap-select2-item__meta ${ hasLongID ? 'uap-select2-item__meta--long' : '' }">(${ sprintf(
					/* translators: 1. Post ID */
					escapeHTML( __( 'ID: %1$s', 'uncanny-automator' ) ),
					optionID.replace( '-objectkey', '' )
				) })</span> ${ template }`;
			}

			if ( ! isEmpty( data.id ) ) {
				// Add the value as an attribute
				template = `<span data-value="${ data.id }">${ template }</span>`;
			}

			return template;
		};

		// Define select2 settings
		const settings = {
			theme: 'uap-select2',
			language: {
				noResults: function() {
					return escapeHTML( __( 'No results found', 'uncanny-automator' ) );
				},
			},
			templateResult: renderHandler,
			templateSelection: renderHandler,
			escapeMarkup: function( markUp ) {
				return markUp;
			},
		};

		// Check if we have to load the options using the search field
		if (
			isDefined( this.attributes.ajax ) &&
			isDefined( this.attributes.ajax.event ) &&
			isDefined( this.attributes.ajax.endpoint ) &&
			this.attributes.ajax.event === 'search_options'
		) {
			// We'll use this to add the selected value, if it's not custom
			const defaultResults = this.parseSelect2Response( [] );

			settings.searchInputPlaceholder = escapeHTML( _x( 'Search', 'Verb', 'uncanny-automator' ) );

			settings.ajax = {
				method: 'POST',
				dataType: 'json',
				url: ajaxurl,

				cache: true,
				delay: 250,

				data: ( params ) => {
					return {
						...{
							action: this.attributes.ajax.endpoint,
							nonce: UncannyAutomator._site.rest.nonce,

							q: params.term,
							// page: params.page || 1,
						},
						...this.getAjaxRequestData( {
							fieldToListenToCode: this.attributes.optionCode
						} )
					};
				},
				processResults: ( response, params ) => {
					this.attributes.ajax.firstLoadDone = true;

					// Check if this is coming from the data adapter
					if ( isDefined( response.results ) && ! isDefined( response.success ) ) {
						return this.parseSelect2Response( response.results );
					}

					// Check if the call was successful
					if ( ! response.success ) {
						return this.parseSelect2Response( [] );
					}

					// Check if the required data is defined
					if ( isEmpty( response.options ) || ! Array.isArray( response.options ) ) {
						return this.parseSelect2Response( [] );
					}

					// Transforms the top-level key of the response object from 'items' to 'results'
					return this.parseSelect2Response( response.options ); // Add pagination here if required
				}
			}

			settings.dataAdapter    = $.fn.select2.amd.require( 'select2/data/extended-ajax' );
			settings.defaultResults = defaultResults;
		}

		// Check if we have to add a placeholder
		if ( this.attributes.hasPlaceholder ) {
			// Add placeholder
			settings.placeholder = this.attributes.placeholder;
		}

		// Check if we don't need the search field
		if ( this.attributes.customClass ) {
			settings.customClass = this.attributes.customClass;
		}

		// Add Clear button if it supports multiple values and custom value.
		if ( this.attributes.supportsMultipleValues && this.attributes.supportsCustomValue ) {
			settings.allowClear = true;
		}

		return settings;
	}

	/**
	 * Init Select2
	 *
	 * @since 0.5
	 */

	initSelect2( $field ) {

		// Already initialized
		if ( $field.hasClass( 'select2-hidden-accessible' ) ) {
			return;
		}

		const settings = this.getSelect2Settings();
		$field.select2( settings );
		
		$field.on( 'select2:open.uap-select-focus', () => {
			// Get the parent container 'form-element'
			const $formElement = $field.closest( '.form-element' );
			// Add focus to the correct search field.
			$formElement.find( '.select2-search__field' ).focus();
		} );

		$field.on( 'select2:open.uap-select-focus', () => {
			// Send custom event to the document
			document.dispatchEvent( new CustomEvent( 'uap/builder/legacy/field/select-opened', {
				detail: {
					fieldID: this.attributes.itemId,
					fieldCode: this.attributes.optionCode,
					fieldType: this.attributes.fieldType,
					integrationCode: this.attributes.integrationCode,
					itemID: this.attributes.itemId,
					value: this.value
				}
			} ) );
		} );

		$field.on( 'select2:close.uap-select-focus', () => {
			// Send custom event to the document
			document.dispatchEvent( new CustomEvent( 'uap/builder/legacy/field/select-closed', {
				detail: {
					fieldID: this.attributes.itemId,
					fieldCode: this.attributes.optionCode,
					fieldType: this.attributes.fieldType,
					integrationCode: this.attributes.integrationCode,
					itemID: this.attributes.itemId,
					value: this.value
				}
			} ) );
		} );

		$field.on( 'change.uap-select-change', () => {
			// Send custom event to the document
			document.dispatchEvent( new CustomEvent( 'uap/builder/legacy/field/change', {
				detail: {
					fieldID: this.attributes.itemId,
					fieldCode: this.attributes.optionCode,
					fieldType: this.attributes.fieldType,
					integrationCode: this.attributes.integrationCode,
					itemID: this.attributes.itemId,
					value: this.value
				}
			} ) );
		} );

		// Check if field has multiple attribute.
		if ( $field.attr( 'multiple' ) ) {
			// Handle backspace key
			$field.on('select2:open', function () {
				// Get the parent container 'form-element'
				const $formElement = $field.closest( '.form-element' );
				// Get the correct search field.
				const $input = $formElement.find( '.select2-search__field' );
				// Attach the keydown event to handle backspace
				$($input).off('keydown').on('keydown', function (e) {
					// Bail if not a backspace or delete key
					if ((e.key !== 'Backspace' && e.key !== 'Delete') || (e.keyCode !== 8 && e.keyCode !== 46)) {
						return;
					}
					// Bail if the input field is not empty.
					if ($($input).val() !== '') {
						return;
					}
					// Prevent default backspace behavior.
					e.preventDefault();
					// Get selected items.
					const selectedData = $field.select2('data');
					// Bail if no selected items
					if (selectedData.length === 0) {
						return;
					}
					// Get the last selected item
					const lastToken = selectedData[selectedData.length - 1];
					// Get current selected IDs
					const currentValues = $field.val();
					// Bail if no last token or current values
					if (!lastToken || !currentValues) {
						return;
					}
					// Remove the last selected item's ID
					const updatedValues = currentValues.filter(value => value !== lastToken.id);
					// Update the Select2 field
					$field.val(updatedValues).trigger('change');
					// Reposition the Select2 dropdown 
					$field.select2('open');
					// Return false to prevent default behavior
					return false;
				});
			});
		}
	}

	/**
	 * Init Flatpickr
	 * Check https://flatpickr.js.org/options/
	 */
	initFlatpickr() {
		// Check if the field exists
		if ( ! isDefined( this.field ) ) {
			return;
		}

		const $field = this.field;

		// Check if it's a date field
		if ( ! [ 'date', 'time' ].includes( this.type ) ) {
			return;
		}

		const initialValue = this.getValue();
		const initialValueIsToken = initialValue && initialValue.includes( '{{' );

		// Check if the we already created the instance before
		this.destroyFlatPickr();

		// Create a global element to add the calendar node
		if ( ! isDefined( document.getElementById( 'uap-flatpickr-calendar' ) ) ) {
			$( 'body' ).append( $( `<div id="uap-flatpickr-calendar" class="uap uap-flatpickr-calendar"></div>` ) );
		}
		const container = document.getElementById( 'uap-flatpickr-calendar' );

		let settings = {
			allowInput: true,

			positionElement: this.$fakeCodemirrorField[ 0 ],

			altInput: true,

			clickOpens: false,

			appendTo: container,

			locale: {
				weekdays: {
					shorthand: [
						/* translators: Abbreviation - Sunday (3 letters) */
						escapeHTML( _x( 'Sun', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - Monday (3 letters) */
						escapeHTML( _x( 'Mon', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - Tuesday (3 letters) */
						escapeHTML( _x( 'Tue', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - Wednesday (3 letters) */
						escapeHTML( _x( 'Wed', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - Thursday (3 letters) */
						escapeHTML( _x( 'Thu', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - Friday (3 letters) */
						escapeHTML( _x( 'Fri', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - Saturday (3 letters) */
						escapeHTML( _x( 'Sat', 'Date format', 'uncanny-automator' ) ),
					],
					longhand: [
						escapeHTML( _x( 'Sunday', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'Monday', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'Tuesday', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'Wednesday', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'Thursday', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'Friday', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'Saturday', 'Date format', 'uncanny-automator' ) ),
					],
				},
				months: {
					shorthand: [
						/* translators: Abbreviation - January (3 letters) */
						escapeHTML( _x( 'Jan', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - February (3 letters) */
						escapeHTML( _x( 'Feb', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - March (3 letters) */
						escapeHTML( _x( 'Mar', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - April (3 letters) */
						escapeHTML( _x( 'Apr', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - May (3 letters) */
						escapeHTML( _x( 'May', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - June (3 letters) */
						escapeHTML( _x( 'Jun', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - July (3 letters) */
						escapeHTML( _x( 'Jul', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - August (3 letters) */
						escapeHTML( _x( 'Aug', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - September (3 letters) */
						escapeHTML( _x( 'Sep', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - October (3 letters) */
						escapeHTML( _x( 'Oct', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - November (3 letters) */
						escapeHTML( _x( 'Nov', 'Date format', 'uncanny-automator' ) ),
						/* translators: Abbreviation - December (3 letters) */
						escapeHTML( _x( 'Dec', 'Date format', 'uncanny-automator' ) ),
					],
					longhand: [
						escapeHTML( _x( 'January', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'February', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'March', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'April', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'May', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'June', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'July', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'August', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'September', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'October', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'November', 'Date format', 'uncanny-automator' ) ),
						escapeHTML( _x( 'December', 'Date format', 'uncanny-automator' ) ),
					],
				},
				rangeSeparator: ` ${ escapeHTML( _x( 'to', 'Date format', 'uncanny-automator' ) ) } `,
				// Numeric representation of the day of the week. 0 (for Sunday) through 6 (for Saturday)
				// In the translatable string we say that 1 is Sunday and Saturday is 7, so we have to subtract 1
				firstDayOfWeek:
					/* translators: Numeric representation of the day of the week. 1 (for Sunday) through 7 (for Saturday) */
					parseInt( escapeHTML( _x( '1', 'Date format', 'uncanny-automator' ) ) ) >= 1 &&
					/* translators: Numeric representation of the day of the week. 1 (for Sunday) through 7 (for Saturday) */
					parseInt( escapeHTML( _x( '1', 'Date format', 'uncanny-automator' ) ) ) <= 7
						/* translators: Numeric representation of the day of the week. 1 (for Sunday) through 7 (for Saturday) */
						? parseInt( escapeHTML( _x( '1', 'Date format', 'uncanny-automator' ) ) ) - 1
						: 0,
				/* translators: Abbreviation - Week */
				weekAbbreviation: ` ${ escapeHTML( _x( 'Wk', 'Date format', 'uncanny-automator' ) ) } `,
				scrollTitle: escapeHTML( __( 'Scroll to increment', 'uncanny-automator' ) ),
				toggleTitle: escapeHTML( __( 'Click to toggle', 'uncanny-automator' ) ),
				amPM: [ escapeHTML( _x( 'AM', 'Date format', 'uncanny-automator' ) ), escapeHTML( _x( 'PM', 'Date format', 'uncanny-automator' ) ) ],
				yearAriaLabel: escapeHTML( _x( 'Year', 'Date format', 'uncanny-automator' ) ),
				monthAriaLabel: escapeHTML( _x( 'Month', 'Date format', 'uncanny-automator' ) ),
				hourAriaLabel: escapeHTML( _x( 'Hour', 'Date format', 'uncanny-automator' ) ),
				minuteAriaLabel: escapeHTML( _x( 'Minute', 'Date format', 'uncanny-automator' ) ),
			},

			parseDate: ( fieldValue, format ) => {
				// Check if it's empty
				if ( isEmpty( fieldValue ) ) {
					return;
				}

				// Format date only if it doesn't contain tokens
				if ( fieldValue.includes( '}}' ) ) {
					return;
				}

				// Parse the date
				return flatpickr.parseDate( fieldValue, format );
			},

			errorHandler: ( error ) => {
				// Check if the field value contains a token
				if ( this.getValue().includes( '}}' ) ) {
					// Don't return any errors, the date is valid
					return;
				}

				// Output error if the developer mode is enabled
				if ( UncannyAutomator._site.has_debug_enabled ) {
					// eslint-disable-next-line no-console
					console.warn( error );
				}
			},

			onReady: ( selectedDates, dateStr, instance ) => {
				// Check if the value of the date field is not equal
				// to the value of the Codemirror field. That would mean
				// that the date field has a valid value, and that we
				// have to format it
				try {
					const formatCodemirrorDate = () => {
						// Check if the field value is not a token
						if ( ! initialValueIsToken ) {
							// Check if the date in the field is formatted differently than
							// the date in the alternative date field
							if ( this.fieldCM.getValue() != instance.altInput.value ) {
								this.fieldCM.setValue( instance.altInput.value );
							}
						}
					};

					// Check if CodeMirror is ready
					if ( isDefined( this.fieldCM ) ) {
						formatCodemirrorDate();
					} else {
						this.field.on( 'ua-codemirror-ready', () => {
							formatCodemirrorDate();
						} );
					}
				} catch ( e ) {
					// Continue regardless of error

					// Output error if the developer mode is enabled
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.warn( e );
					}
				}
			},

			onChange: ( selectedDates, dateStr, instance ) => {
				// Update the Codemirror field
				try {
					const setCodemirrorValue = () => {
						// Remove the current value from the Codemirror field,
						// and set the value of the alt date/time field as value
						this.fieldCM.setValue( instance.altInput.value );
					};

					// Check if CodeMirror is ready
					if ( isDefined( this.fieldCM ) ) {
						setCodemirrorValue();
					} else {
						this.field.on( 'ua-codemirror-ready', () => {
							setCodemirrorValue();
						} );
					}
				} catch ( e ) {
					// Continue regardless of error

					// Output error if the developer mode is enabled
					if ( UncannyAutomator._site.has_debug_enabled ) {
						// eslint-disable-next-line no-console
						console.warn( e );
					}
				}
			},
		};

		// Settings depending on the field type
		// https://github.com/flatpickr/flatpickr/blob/master/src/types/options.ts#L19
		if ( this.attributes.fieldType == 'date' ) {
			settings = $.extend( settings, {
				// https://flatpickr.js.org/formatting/
				dateFormat: this.attributes.dateFormat,
				altFormat: this.attributes.dateAltFormat,
			} );

			// Check if it supports a range
			if ( this.attributes.supportsRange ) {
				settings.mode = 'range';
			}

			// Check if it has a min date
			if ( isDefined( this.attributes.minDate ) ) {
				settings.minDate = this.attributes.minDate;
			}

			// Check if it has a max date
			if ( isDefined( this.attributes.maxDate ) ) {
				settings.maxDate = this.attributes.maxDate;
			}
		}

		if ( this.attributes.fieldType == 'time' ) {

			// Check if it's using a 12-hour format
			const is12HourFormat = this.attributes.timeAltFormat.includes( 'K' );

			settings = $.extend( settings, {
				enableTime: true,
				noCalendar: true,

				// https://flatpickr.js.org/formatting/
				dateFormat: this.attributes.timeFormat,
				altFormat: this.attributes.timeAltFormat,

				// Check the alternative format (the format from WordPress) is using either `a` or `A` (K in our code, since that's what flatpickr uses)
				time_24hr: ! is12HourFormat,
			} );

			// Check if it has a minute increment
			if ( isDefined( this.attributes.minuteIncrement ) ) {
				settings.minuteIncrement = this.attributes.minuteIncrement;
			}

			// Check if it has a min time
			if ( isDefined( this.attributes.minTime ) ) {
				settings.minTime = this.attributes.minTime;
			}

			// Check if it has a max time
			if ( isDefined( this.attributes.manTime ) ) {
				settings.manTime = this.attributes.manTime;
			}
		}

		// Init flatpickr
		this.flatpickrInstance = $field.flatpickr( settings );
		// When using a token and the enableTime option is set to true,
		// flatpicker removes the value from the input field
		// This is a workaround to reset the value again
		if ( this.attributes.fieldType == 'time' && initialValueIsToken ) {
			this.flatpickrInstance.input.value = initialValue;
		}
	}

	/**
	 * Destroys the flatpickr instance
	 */
	destroyFlatPickr() {
		// Check if the we already created the instance before
		if ( ! isDefined( this.flatpickrInstance ) ) {
			return;
		}

		this.flatpickrInstance.destroy();
	}

	/**
	 * Init CodeMirror
	 *
	 * @since 3.0
	 */

	initCodeMirror() {
		// Check if this isn't already a CodeMirror instance
		if (
			[ 'text', 'url', 'email', 'int', 'float', 'date', 'time', 'textarea' ].includes( this.attributes.fieldType )
		) {
			// Check if we already created a CodeMirror for this field
			this.destroyCodeMirror();

			const initialValue = this.getValue();

			// Set options
			let CodeMirrorOptions = {
				lineNumbers: false,

				tabSize: 4,
				value: initialValue,

				mode: 'default',
				dragDrop: false,
				viewportMargin: Infinity,
				lineWrapping: true,
				autoRefresh: true,

				direction: ! isEmpty( UncannyAutomator._site.is_rtl ) && UncannyAutomator._site.is_rtl ? 'rtl' : 'ltr',

				// Only allow breaklines in textareas
				noNewlines: true,
				// Only allow scrollbars in textareas
				scrollbarStyle: null,

				readOnly: this.attributes.isReadOnly,
			};

			// Make exceptions for textarea
			if ( this.attributes.fieldType == 'textarea' ) {
				CodeMirrorOptions = Object.assign( CodeMirrorOptions, {
					scrollbarStyle: 'native',
					noNewlines: false
				} );

				// If TinyMCE is enabled, show the line numbers
				if ( this.attributes.supportsTinyMCE ) {
					CodeMirrorOptions = Object.assign( CodeMirrorOptions, {
						lineNumbers: true,
						mode: 'htmlmixed',
						verify_html: false,
						inline_styles: true,
					} );
				}
			}

			// Disable tab for all fields except textareas
			if ( this.attributes.fieldType != 'textarea' ) {
				CodeMirrorOptions.extraKeys = {
					Tab: () => {
						// Get next field
						const nextField = this.getNextField();

						// Check if there is a next field
						if ( isDefined( nextField ) ) {
							// Focus the field
							nextField.focus();
						}

						return false;
					}
				}
			}

			// CodeMirror
			this.fieldCM = CodeMirror( this.$fakeCodemirrorField[ 0 ], CodeMirrorOptions );

			// Add the placeholder
			if ( this.attributes.hasPlaceholder ) {
				this.fieldCM.setOption( 'placeholder', this.attributes.placeholder );
			}

			this.fieldCM.on( 'changes', ( event, source ) => {
				// Check if the current field is NOT a date or time field
				if ( ! [ 'date', 'time' ].includes( this.attributes.fieldType ) ) {
					this.setValue( this.fieldCM.getValue(), source[0].origin !== 'setValue', {
						updateCodemirror: false,
					} );
				}
			} );

			this.fieldCM.on( 'focus', () => {
				// If it's a date or time field, try to open the calendar
				if ( [ 'date', 'time' ].includes( this.attributes.fieldType ) ) {
					try {
						// Open calendar
						setTimeout( () => {
							this.flatpickrInstance.open();
						}, 50 );
					} catch ( e ) {
						// Continue regardless of error

						// Output error if the developer mode is enabled
						if ( UncannyAutomator._site.has_debug_enabled ) {
							// eslint-disable-next-line no-console
							console.warn( e );
						}
					}
				}
			} );

			this.field.on( 'focus', () => {
				this.fieldCM.focus();
			} );

			this.field.trigger( 'ua-codemirror-ready' );
		}

		if ( this.attributes.fieldType == 'select' ) {
			// Check if it has a custom value
			if ( isDefined( this.customValueField ) ) {
				this.customValueField.initCodeMirror();
			}
		}
	}

	/**
	 * Get the field below the current field
	 *
	 * @since 3.0
	 */

	getNextField() {
		let nextField = null;

		// Check if this field is part of an item
		if ( isDefined( this.attributes.itemId ) && isDefined( this.attributes.groupId ) ) {
			// Get the group of fields
			const groupOfFields =
				UncannyAutomator.recipe.items[ this.attributes.itemId ].options[ this.attributes.groupId ].fields;

			// Check if it's defined and is an array
			if ( isDefined( groupOfFields ) && $.isArray( groupOfFields ) ) {
				// Get the position of the current field
				const fieldPosition = groupOfFields.indexOf( this );

				// Check if we found the field
				if ( fieldPosition != -1 ) {
					// Check if it's NOT the last field
					if ( fieldPosition != groupOfFields.length - 1 ) {
						// Get the next field
						nextField = groupOfFields[ fieldPosition + 1 ];
					}
				}
			}
		}

		// Check if it's the user selector
		if ( isDefined( this.attributes.itemType ) && this.attributes.itemType == 'user-selector' ) {
			// Get fields
			const groupOfFields = UncannyAutomator.recipe.userSelector.fields;

			// Check if it's defined and is an array
			if ( isDefined( groupOfFields ) && $.isArray( groupOfFields ) ) {
				// Get the position of the current field
				const fieldPosition = groupOfFields.indexOf( this );

				// Check if we found the field
				if ( fieldPosition != -1 ) {
					// Check if it's NOT the last field
					if ( fieldPosition != groupOfFields.length - 1 ) {
						// Get the next field
						nextField = groupOfFields[ fieldPosition + 1 ];
					}
				}
			}
		}

		return nextField;
	}

	/**
	 * Focus the current field
	 *
	 * @since 3.0
	 */

	focus() {
		if ( isDefined( this.field ) ) {
			switch ( this.type ) {
				case 'select':
					// Try to open the select2 field
					try {
						this.field.select2( 'focus' );
					} catch ( e ) {
						// Otherwise, just try to focus the normal dropdown
						this.field.focus();
					}
					break;

				case 'text':
				case 'url':
				case 'email':
				case 'int':
				case 'float':
				case 'date':
				case 'time':
					// Check if it's using CodeMirror
					if ( isDefined( this.fieldCM ) ) {
						this.fieldCM.focus();
					} else {
						this.field.focus();
					}

					break;

				case 'textarea':
					// Check if it's using TinyMCE
					if ( isDefined( this.fieldTinyMCE ) ) {
						// Check if it's using the "Text" tab
						if ( this.field.is( ':visible' ) ) {
							this.field.focus();
						} else {
							try {
								// Focus the TinyMCE field
								this.fieldTinyMCE.focus();
							} catch ( e ) {
								// Continue regardless of error

								// Output error if the developer mode is enabled
								if ( UncannyAutomator._site.has_debug_enabled ) {
									// eslint-disable-next-line no-console
									console.warn( e );
								}
							}
						}
					} else {
						this.field.focus();
					}

					break;

				case 'checkbox':
					this.field.focus();
					break;

				case 'radio':
					this.field.focus();
					break;
			}
		}
	}

	/**
	 * Destroy CodeMirror
	 *
	 * @since 3.0
	 */
	destroyCodeMirror() {
		// Check if we already created a CodeMirror for this field
		if ( isDefined( this.fieldCM ) ) {
			try {
				const $field = this.fieldCM.getWrapperElement();
				$field.parentNode.removeChild( $field );
				this.fieldCM = null;
			} catch ( e ) {
				// Output error if the developer mode is enabled
				if ( UncannyAutomator._site.has_debug_enabled ) {
					// eslint-disable-next-line no-console
					console.warn( e );
				}
			}
		}
	}

	/**
	 * Select
	 * Rerender the dropdown
	 */

	rerenderSelect2() {
		const settings = this.getSelect2Settings();

		if ( ! isEmpty( this.field ) && this.field.hasClass( 'select2-hidden-accessible' ) ) {
			this.field.select2( 'destroy' ).select2( settings );
		}
	}

	/**
	 * Init Token Selector
	 *
	 * @since 3.0
	 */

	initTokenSelector() {
		if ( this.attributes.supportsTokens ) {
			// Check if we already created the token selector
			this.destroyTokenSelector();

			// Create object for the token selector
			const options = {
				unsupportedTokens: this.attributes.unsupportedTokens,
				onlyLoopableTokens: this.attributes.onlyLoopableTokens
			}

			// Check if this is an item field
			if ( ! isEmpty( this.attributes.itemId ) ) {
				options.itemID = this.attributes.itemId;
			}

			this.tokenSelector = new TokenSelector( this, options );
		}

		if ( this.attributes.fieldType == 'select' ) {
			// Check if it has a custom value
			if ( isDefined( this.customValueField ) ) {
				this.customValueField.initTokenSelector();
			}
		}
	}

	/**
	 * Destroy the Token selector
	 *
	 * @since 3.0
	 */

	destroyTokenSelector() {
		// Check if we already created the token selector
		if ( isDefined( this.tokenSelector ) ) {
			this.tokenSelector.destroy();
		}
	}

	/**
	 * Init TinyMCE
	 * We're going to call this in ItemOptions.js because TinyMCE needs the item in the DOM before changing it
	 *
	 * @since 0.5
	 */

	initTinyMCE() {
		const $textarea = this.field;

		// TinyMCE
		if ( isDefined( this.field ) && this.attributes.fieldType == 'textarea' && this.attributes.supportsTinyMCE ) {
			// Get field ID
			const fieldId = this.field.prop( 'id' );

			// Get editor
			let editor = tinymce.get( fieldId );

			if ( isDefined( editor ) ) {
				wp.editor.remove( fieldId );
			}

			// Prepare TinyMCE styles
			const tinyMCEStyles = [ ...UncannyAutomator._site.automator.links.styles_for_tinymce ];
			try {
				tinyMCEStyles.push( wp.editor.getDefaultSettings().tinymce.content_css );
			} catch ( e ) {
				if ( UncannyAutomator._site.has_debug_enabled ) {
					// eslint-disable-next-line no-console
					console.warn( `Field: Couldn't load WordPress's custom assets for TinyMCE` );
				}
			}
			
			// Generate TinyMCE configuration using utility function
			const tinyOptions = generateTinyMCEConfig( this.attributes, tinyMCEStyles );

			// Init TinyMCE
			wp.editor.initialize( fieldId, tinyOptions );

			// Get editor
			editor = tinymce.get( fieldId );

			// Check what's the current active tab
			// If .hidden is true, then the active tab is "Text"
			if ( editor.hidden ) {
				this.fieldContainer.addClass( 'form-textarea--tinymce-text' );
			} else {
				this.fieldContainer.addClass( 'form-textarea--tinymce-visual' );
			}

			// Detect changes on the "Visual" and "Text" tabs
			this.formElement.find( '.switch-tmce' ).on( 'click', () => {
				this.field.trigger( 'ua-tinymce-activate-visual-view' );

				// Change class to indicate what's the activate tab
				this.fieldContainer
					.removeClass( 'form-textarea--tinymce-text' )
					.addClass( 'form-textarea--tinymce-visual' );
			} );

			this.formElement.find( '.switch-html' ).on( 'click', () => {
				this.field.trigger( 'ua-tinymce-activate-text-view' );

				// Change class to indicate what's the activate tab
				this.fieldContainer
					.removeClass( 'form-textarea--tinymce-visual' )
					.addClass( 'form-textarea--tinymce-text' );

				// Set the value of the normal field
				// But don't trigger the change event
				try {
					this.fieldCM.setValue(
						editor.getContent( {
							format: 'raw',
						} )
					);
				} catch ( e ) {
					this.field.val(
						editor.getContent( {
							format: 'raw',
						} )
					);
				}

				// Refresh the field
				this.fieldCM.refresh();
			} );

			this.fieldTinyMCE = editor;

			editor.on( 'init', () => {
				this.field.trigger( 'ua-tinymce-ready' );
			} );
		}
	}

	/**
	 * Applies dynamic visibility rules to the field.
	 */
	applyDynamicVisibility() {
		const visibility = this.attributes.dynamic_visibility;
		let isVisible = visibility.default_state === 'visible';

		// Iterate over each visibility rule
		visibility.visibility_rules.forEach( rule => {
			const ruleResult = this.evaluateVisibilityRule( rule );
			if ( ruleResult ) {
				isVisible = rule.resulting_visibility === 'show';
			}
		} );

		// Update field visibility based on evaluated rules
		this.updateFieldVisibility( isVisible );
	}

	/**
	 * Evaluates a single visibility rule.
	 * @param {Object} rule - The visibility rule to evaluate.
	 * @return {Boolean} - Result of the rule evaluation.
	 */
	evaluateVisibilityRule( rule ) {
		return rule.rule_conditions[ rule.operator === 'AND' ? 'every' : 'some' ]( condition => {
			const fieldValue = getField( this.attributes.itemId, condition.option_code, this.attributes.itemType ).getValue();
			return this.compareValues( fieldValue, condition.value, condition.compare );
		} );
	}

	/**
	 * Updates the visibility of the field.
	 * @param {Boolean} isVisible - Whether the field should be visible.
	 */
	updateFieldVisibility( isVisible ) {
		// Update the field's visibility attribute
		this.attributes.isHidden = ! isVisible;

		try {
			if ( isVisible ) {
				this.fieldContainer.removeClass( 'form-element--hidden' );
			} else {
				this.fieldContainer.addClass( 'form-element--hidden' );
			}
		} catch ( e ) {
			// console.warn( e );
		}
	}

	/**
	 * Compares two values based on a specified operator.
	 * @param {*} value1 - The first value to compare.
	 * @param {*} value2 - The second value to compare.
	 * @param {String} operator - The comparison operator.
	 * @return {Boolean} - Result of the comparison.
	 */
	compareValues( value1, value2, operator ) {
		switch ( operator ) {
			case '==': return value1 == value2;
			case '!=': return value1 != value2;
			// Add more comparison operators as needed
			default: return false;
		}
	}

	/**
	 * Sets up change listeners on parent fields that affect this field's visibility.
	 */
	setupVisibilityChangeListener() {
		const visibility = this.attributes.dynamic_visibility;

		visibility.visibility_rules.forEach( rule => {
			rule.rule_conditions.forEach( condition => {
				const parentField = getField( this.attributes.itemId, condition.option_code, this.attributes.itemType );
				if ( parentField ) {
					parentField.field.on( 'change', () => {
						this.applyDynamicVisibility();
					} );
				}
			} );
		} );
	}

}

export default Field;

/**
 * Extends select2 so it supports having pre-defined options in fields using AJAX
 */
$.fn.select2.amd.define( 'select2/data/extended-ajax', [ './ajax', './tags', '../utils', 'module', 'jquery' ], function( AjaxAdapter, Tags, Utils, module, $ ) {

	/**
	 *
	 * @param $element
	 * @param options
	 */
	function ExtendedAjaxAdapter( $element, options ) {
		// we need explicitly process minimumInputLength value
		// to decide should we use AjaxAdapter or return defaultResults,
		// so it is impossible to use MinimumLength decorator here
		this.minimumInputLength = options.get( 'minimumInputLength' );
		this.defaultResults = options.get( 'defaultResults' );

		ExtendedAjaxAdapter.__super__.constructor.call( this, $element, options );
	}

	Utils.Extend( ExtendedAjaxAdapter, AjaxAdapter );

	// override original query function to support default results
	var originQuery = AjaxAdapter.prototype.query;

	ExtendedAjaxAdapter.prototype.query = function( params, callback ) {
		var defaultResults = ( typeof this.defaultResults == 'function' ) ? this.defaultResults.call( this ) : this.defaultResults;
		if ( ( defaultResults && defaultResults.length ) && ( ! params.term || params.term.length < this.minimumInputLength ) && ( params._type && params._type == 'query' ) ) {
			var data = { results: defaultResults };
			var processedResults = this.processResults( data, params );
			callback( processedResults );
		} else  {
			originQuery.call( this, params, callback );
		}
	};

	if ( module.config().tags ) {
		return Utils.Decorate( ExtendedAjaxAdapter, Tags );
	} else {
		return ExtendedAjaxAdapter;
	}
} );